import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Grid,
  LinearProgress,
  Typography,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  Apple as AppleIcon,
  Android as AndroidIcon,
  Edit as EditIcon,
  Translate as TranslateIcon,
  Refresh as RefreshIcon,
  NewReleases as NewReleasesIcon,
  AutoFixHigh as AutoFixHighIcon,
} from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useAuth } from "../hooks/useAuth";
import { useToastStateSync } from "../hooks/useToastStateSync";
import AIService from "../services/AIService";
import { ListingService } from "../services/ListingService";
import AppService from "../services/AppService";
import IconService from "../services/IconService";
import ScreenshotService from "../services/ScreenshotService";
import { buildAppContextLines } from "../utils/appContextPrompt";
import {
  filterStoreLocalesBySupport,
  getStoreLocaleOption,
  getStoreLocaleDisplayLabel,
  STORE_LOCALE_SELECTION_OPTIONS,
  type StoreLocaleSupportFilter,
} from "../utils/storeLocales";
import { APP_CATEGORIES, formatAppCategoryLabel } from "../utils/appCategories";
import type {
  AndroidListingSchema,
  AppStoreConnectPublishResponse,
  AppStoreConnectValidationResponse,
  AppStoreConnectWhatsNewLocaleItem,
  AppStoreListing as AppStoreListingType,
  CreateListingDTO,
  FetchAppStoreConnectWhatsNewResponse,
  GooglePlayPublishMode,
  GooglePlayPublishResponse,
  GooglePlayValidationResponse,
  IOSListingSchema,
  ListingLocaleEntry,
  UpdateAppStoreConnectWhatsNewResponse,
} from "../types/listing";
import type { App } from "../types/app";
import type { GeneratedIcon } from "../types/icon";
import type { StoredStoreScreenshotAsset } from "../types/screenshot";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export const AVAILABLE_LANGUAGES = STORE_LOCALE_SELECTION_OPTIONS;

const GOOGLE_PLAY_LOCALE_REGEX = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;
const AI_LISTING_MODELS = [
  { value: "gemini-3-flash-preview", label: "Gemini 3 Flash Preview" },
  { value: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash Lite" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
  { value: "gpt-4.1", label: "OpenAI GPT-4.1" },
  { value: "gpt-4.1-mini", label: "OpenAI GPT-4.1 Mini" },
] as const;

type EditableListingFieldKey =
  | "title"
  | "subtitle"
  | "promotionalText"
  | "supportUrl"
  | "marketingUrl"
  | "whatsNew"
  | "description"
  | "shortDescription"
  | "fullDescription"
  | "keywords";

type WhatsNewTemplateKey =
  | "bug_fixes_improvements"
  | "new_features"
  | "stability_performance"
  | "custom";

type ListingContextPresetKey = "medical_disclaimer" | "hard_paywall";

type EditableListingFieldConfig = {
  key: EditableListingFieldKey;
  label: string;
  maxLength?: number;
  multiline?: boolean;
  minRows?: number;
  helperText: string;
  aiTaskLabel: string;
};

type PublishSectionKey =
  | "overview"
  | "target"
  | "validate"
  | "preview"
  | "confirm"
  | "result";

type ValidationPreviewLayout = "stacked" | "side_by_side";
type CreateListingFlowStage =
  | "form"
  | "generating_base"
  | "base_review"
  | "generating"
  | "success";
type ListingPublishResponse =
  | GooglePlayPublishResponse
  | AppStoreConnectPublishResponse;
type ListingValidationResponse =
  | GooglePlayValidationResponse
  | AppStoreConnectValidationResponse;

const IOS_EDITABLE_FIELDS: EditableListingFieldConfig[] = [
  {
    key: "title",
    label: "Title",
    maxLength: 30,
    helperText: "App Store title. Keep it concise and brand-led.",
    aiTaskLabel: "App Store title",
  },
  {
    key: "subtitle",
    label: "Subtitle",
    maxLength: 30,
    helperText: "Short supporting value proposition under the title.",
    aiTaskLabel: "App Store subtitle",
  },
  {
    key: "promotionalText",
    label: "Promotional Text",
    maxLength: 170,
    multiline: true,
    minRows: 3,
    helperText:
      "Optional top-of-page promotional copy for updates or campaigns.",
    aiTaskLabel: "App Store promotional text",
  },
  {
    key: "supportUrl",
    label: "Support URL",
    helperText:
      "Localized support URL shown in App Store version metadata. Must be a valid https URL.",
    aiTaskLabel: "App Store support URL",
  },
  {
    key: "marketingUrl",
    label: "Marketing URL",
    helperText:
      "Localized marketing URL shown in App Store version metadata. Must be a valid https URL.",
    aiTaskLabel: "App Store marketing URL",
  },
  {
    key: "whatsNew",
    label: "What's New",
    maxLength: 4000,
    multiline: true,
    minRows: 5,
    helperText:
      "Release notes shown in App Store updates. Not used for first app versions.",
    aiTaskLabel: "App Store What's New release notes",
  },
  {
    key: "description",
    label: "Description",
    maxLength: 4000,
    multiline: true,
    minRows: 8,
    helperText:
      "Full App Store description with clear conversion-focused structure.",
    aiTaskLabel: "App Store description",
  },
  {
    key: "keywords",
    label: "Keywords",
    helperText: "Comma-separated keyword list. Max 100 total characters.",
    aiTaskLabel: "App Store keywords",
  },
];

const ANDROID_EDITABLE_FIELDS: EditableListingFieldConfig[] = [
  {
    key: "title",
    label: "Title",
    maxLength: 30,
    helperText: "Google Play title. Focus on clear positioning and relevance.",
    aiTaskLabel: "Google Play title",
  },
  {
    key: "shortDescription",
    label: "Short Description",
    maxLength: 80,
    multiline: true,
    minRows: 3,
    helperText: "Concise high-conversion summary shown above the fold.",
    aiTaskLabel: "Google Play short description",
  },
  {
    key: "fullDescription",
    label: "Full Description",
    maxLength: 4000,
    multiline: true,
    minRows: 8,
    helperText:
      "Long-form Play Store description optimized for conversion and clarity.",
    aiTaskLabel: "Google Play full description",
  },
];

const GOOGLE_PLAY_FIELD_LIMITS = {
  title: 30,
  shortDescription: 80,
  fullDescription: 4000,
} as const;
const CREATE_LISTING_STATUS_MESSAGES = [
  "Preparing listing context and validation checks...",
  "Generating localized store copy for the selected targets...",
  "Normalizing fields for store-safe formatting...",
  "Saving generated listing versions for preview and publishing...",
] as const;
const BASE_DRAFT_STATUS_MESSAGES = [
  "Preparing listing context and validation checks...",
  "Generating base English listing copy...",
  "Normalizing base fields for store-safe formatting...",
  "Preparing base copy for locale generation...",
] as const;
const APP_STORE_KEYWORDS_MAX_CHARS = 100;
const WHATS_NEW_TEMPLATE_OPTIONS: Array<{
  value: WhatsNewTemplateKey;
  label: string;
  baseText: string;
  description: string;
}> = [
  {
    value: "bug_fixes_improvements",
    label: "Bug fixes and improvements",
    baseText:
      "This update includes bug fixes and general improvements to make the app more reliable and easier to use.",
    description: "Safe default for routine updates and maintenance releases.",
  },
  {
    value: "new_features",
    label: "New features for you to enjoy",
    baseText:
      "We added new features and improved key flows to help you get more value from the app.",
    description: "Use when the release introduces visible product updates.",
  },
  {
    value: "stability_performance",
    label: "Performance and stability",
    baseText:
      "This release improves performance and stability, including faster loading and smoother app behavior.",
    description: "Use for optimization-focused releases.",
  },
  {
    value: "custom",
    label: "Custom text",
    baseText: "",
    description:
      "Write your own source note. It will be localized into selected locales.",
  },
];

const LISTING_CONTEXT_TEMPLATE_OPTIONS: Array<{
  value: ListingContextPresetKey;
  label: string;
  baseText: string;
  description: string;
}> = [
  {
    value: "medical_disclaimer",
    label: "Medical disclaimer",
    baseText:
      "Medical disclaimer: This app is for informational and educational purposes only and is not a substitute for professional medical advice, diagnosis, or treatment.",
    description:
      "Useful for health-related apps that need a clear non-medical-advice disclaimer.",
  },
  {
    value: "hard_paywall",
    label: "Subscription required for key features",
    baseText:
      "Subscription notice: Core features require an active subscription. Please review pricing and terms in-app before subscribing.",
    description: "Useful when most value is behind a paid subscription.",
  },
];

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const AppStoreListingPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [listings, setListings] = useState<AppStoreListingType[]>([]);
  const [selectedListing, setSelectedListing] =
    useState<AppStoreListingType | null>(null);
  const [previewMode, setPreviewMode] = useState<"structured" | "frame">(
    "structured",
  );
  const [previewEditMode, setPreviewEditMode] = useState(false);
  const [previewLocale, setPreviewLocale] = useState<string>("");
  const [listingIcons, setListingIcons] = useState<GeneratedIcon[]>([]);
  const [selectedListingIconUrl, setSelectedListingIconUrl] =
    useState<string>("");
  const [storeFrameAssets, setStoreFrameAssets] = useState<
    StoredStoreScreenshotAsset[]
  >([]);
  const [storeFrameLatestVersion, setStoreFrameLatestVersion] = useState<
    number | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  useToastStateSync({ success, error });
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [newListingDialog, setNewListingDialog] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishTargetListing, setPublishTargetListing] =
    useState<AppStoreListingType | null>(null);
  const [publishSections, setPublishSections] = useState<
    Record<PublishSectionKey, boolean>
  >({
    overview: true,
    target: true,
    validate: true,
    preview: false,
    confirm: true,
    result: false,
  });
  const [validationPreviewLayout, setValidationPreviewLayout] =
    useState<ValidationPreviewLayout>("stacked");
  const [publishMode, setPublishMode] =
    useState<GooglePlayPublishMode>("all_locales");
  const [publishLocaleFilter, setPublishLocaleFilter] = useState<
    "supported" | "all_generated"
  >("supported");
  const [publishLocale, setPublishLocale] = useState<string>("");
  const [publishSelectedLocales, setPublishSelectedLocales] = useState<
    string[]
  >([]);
  const [validatingGooglePlay, setValidatingGooglePlay] = useState(false);
  const [publishingToGooglePlay, setPublishingToGooglePlay] = useState(false);
  const [publishResult, setPublishResult] =
    useState<ListingPublishResponse | null>(null);
  const [validationResult, setValidationResult] =
    useState<ListingValidationResponse | null>(null);
  const [publishConfirmationChecked, setPublishConfirmationChecked] =
    useState(false);
  const [publishIgnoreValidationIssues, setPublishIgnoreValidationIssues] =
    useState(false);
  const [publishSkipValidationOverride, setPublishSkipValidationOverride] =
    useState(false);
  const [editableEntryContent, setEditableEntryContent] = useState<
    IOSListingSchema | AndroidListingSchema | null
  >(null);
  const [savingEntryContent, setSavingEntryContent] = useState(false);
  const [optimizingFieldKey, setOptimizingFieldKey] = useState<string | null>(
    null,
  );
  const [fixingLocaleIssueKey, setFixingLocaleIssueKey] = useState<
    string | null
  >(null);
  const [bulkFixingIssues, setBulkFixingIssues] = useState(false);
  const [bulkFixProgress, setBulkFixProgress] = useState<{
    currentLocale: string | null;
    completed: number;
    total: number;
  }>({
    currentLocale: null,
    completed: 0,
    total: 0,
  });
  const [bulkFixLocaleStatuses, setBulkFixLocaleStatuses] = useState<
    Record<string, "success" | "error">
  >({});
  const [translatingFieldKey, setTranslatingFieldKey] = useState<string | null>(
    null,
  );
  const [englishFieldTranslations, setEnglishFieldTranslations] = useState<
    Record<
      string,
      { sourceValue: string; translatedText: string; model: string }
    >
  >({});
  const [listingAiModel, setListingAiModel] = useState<string>(
    "gemini-3-flash-preview",
  );
  const [selectedApp, setSelectedApp] = useState<string>("");
  const [listingFilterAppId, setListingFilterAppId] = useState<string>("");
  const [apps, setApps] = useState<App[]>([]);
  const [createSections, setCreateSections] = useState({
    appSource: true,
    outputTarget: true,
    appContext: false,
    locales: false,
  });
  const [createListingModel, setCreateListingModel] = useState<string>(
    "gemini-2.5-flash-lite",
  );
  const [createListingStage, setCreateListingStage] =
    useState<CreateListingFlowStage>("form");
  const [createListingStatusIndex, setCreateListingStatusIndex] = useState(0);
  const [createdListingsResult, setCreatedListingsResult] = useState<
    AppStoreListingType[]
  >([]);
  const [baseListingDrafts, setBaseListingDrafts] = useState<
    Partial<Record<"ios" | "android", IOSListingSchema | AndroidListingSchema>>
  >({});
  const [baseReviewPlatform, setBaseReviewPlatform] = useState<
    "ios" | "android"
  >("ios");
  const [expandedPreviewDateGroups, setExpandedPreviewDateGroups] = useState<
    Record<string, boolean>
  >({});
  const [expandedListingsDateGroups, setExpandedListingsDateGroups] = useState<
    Record<string, boolean>
  >({});
  const [localeSupportFilter, setLocaleSupportFilter] =
    useState<StoreLocaleSupportFilter>("common");
  const [localeSearchQuery, setLocaleSearchQuery] = useState("");
  const previewLocaleCardRef = useRef<HTMLDivElement | null>(null);

  const [formData, setFormData] = useState({
    appName: "",
    category: APP_CATEGORIES[0] as string,
    features: ["", "", ""],
    platform: "ios" as "ios" | "android" | "both",
    locales: [] as string[],
    targetAudience: "",
    keywords: ["", "", ""],
    supportUrl: "",
    marketingUrl: "",
    usp: ["", ""],
    includeWhatsNew: true,
    isFirstVersionApp: false,
    whatsNewTemplate: "bug_fixes_improvements" as WhatsNewTemplateKey,
    whatsNewCustomText: "",
    listingContextTemplates: [] as ListingContextPresetKey[],
    listingContextCustomText: "",
  });

  // What's New Updater tab state
  const [whatsNewAppId, setWhatsNewAppId] = useState<string>("");
  const [whatsNewFetching, setWhatsNewFetching] = useState(false);
  const [whatsNewFetchResult, setWhatsNewFetchResult] =
    useState<FetchAppStoreConnectWhatsNewResponse | null>(null);
  const [whatsNewBaseText, setWhatsNewBaseText] = useState<string>("");
  const [whatsNewTranslations, setWhatsNewTranslations] = useState<
    Record<string, string>
  >({});
  const [whatsNewTranslating, setWhatsNewTranslating] = useState(false);
  const [whatsNewPushing, setWhatsNewPushing] = useState(false);
  const [whatsNewPushResult, setWhatsNewPushResult] =
    useState<UpdateAppStoreConnectWhatsNewResponse | null>(null);
  const [whatsNewError, setWhatsNewError] = useState<string | null>(null);
  const [whatsNewSuccess, setWhatsNewSuccess] = useState<string | null>(null);
  const isIosCapableApp = (app: App) => {
    const platform = String(app.platform || "")
      .trim()
      .toLowerCase();
    return !platform || platform === "ios" || platform === "both";
  };

  const effectiveIncludeWhatsNew =
    formData.platform !== "android" &&
    formData.includeWhatsNew &&
    !formData.isFirstVersionApp;
  const getWhatsNewTemplate = (value: WhatsNewTemplateKey) =>
    WHATS_NEW_TEMPLATE_OPTIONS.find((item) => item.value === value) ||
    WHATS_NEW_TEMPLATE_OPTIONS[0];
  const resolveWhatsNewBaseText = () => {
    const selectedTemplate = getWhatsNewTemplate(formData.whatsNewTemplate);
    const customText = String(formData.whatsNewCustomText || "").trim();
    if (formData.whatsNewTemplate === "custom") {
      return customText;
    }
    return customText || selectedTemplate.baseText;
  };
  const getListingContextTemplate = (value: ListingContextPresetKey) =>
    LISTING_CONTEXT_TEMPLATE_OPTIONS.find((item) => item.value === value) ||
    LISTING_CONTEXT_TEMPLATE_OPTIONS[0];
  const listingContextCustomText = String(
    formData.listingContextCustomText || "",
  ).trim();
  const resolveListingContextBaseText = () => {
    const selectedTemplateNotes = formData.listingContextTemplates
      .map((templateKey) => getListingContextTemplate(templateKey)?.baseText)
      .map((value) => String(value || "").trim())
      .filter(Boolean);
    return [
      ...selectedTemplateNotes,
      ...(listingContextCustomText ? [listingContextCustomText] : []),
    ].join("\n\n");
  };
  const hasListingContextNote =
    String(resolveListingContextBaseText() || "").trim().length > 0;
  const listingContextSummaryLabel = hasListingContextNote
    ? `Context note included (${[
        formData.listingContextTemplates.length > 0
          ? `${formData.listingContextTemplates.length} preset${formData.listingContextTemplates.length === 1 ? "" : "s"}`
          : "",
        listingContextCustomText ? "custom" : "",
      ]
        .filter(Boolean)
        .join(" + ")})`
    : "No context note";
  const filteredAvailableLanguages = useMemo(() => {
    const normalizedQuery = localeSearchQuery.trim().toLowerCase();

    return filterStoreLocalesBySupport(AVAILABLE_LANGUAGES, localeSupportFilter)
      .filter((language) => {
        if (!normalizedQuery) {
          return true;
        }

        const searchableText = [
          language.code,
          language.name,
          getStoreLocaleDisplayLabel(language.code),
        ]
          .join(" ")
          .toLowerCase();

        return searchableText.includes(normalizedQuery);
      })
      .sort((left, right) =>
        getStoreLocaleDisplayLabel(left.code).localeCompare(
          getStoreLocaleDisplayLabel(right.code),
          undefined,
          { sensitivity: "base" },
        ),
      );
  }, [localeSearchQuery, localeSupportFilter]);
  const toStringArray = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is string => typeof item === "string");
  };

  const loadListings = useCallback(async () => {
    if (!user?.email) return;
    try {
      setLoading(true);
      const userListings = await ListingService.getUserListings(user.email);
      const normalizedListings = userListings.map((listing) => {
        const safeEntries = Array.isArray((listing as any).entries)
          ? (listing as any).entries
          : [];
        const safeLocales = Array.isArray((listing as any).locales)
          ? (listing as any).locales
          : safeEntries
              .map((entry: any) => entry?.locale)
              .filter(
                (locale: unknown): locale is string =>
                  typeof locale === "string" && locale.length > 0,
              );
        return {
          ...listing,
          locales: safeLocales,
          entries: safeEntries,
        } as AppStoreListingType;
      });
      setListings(normalizedListings);
    } catch (err) {
      setError("Failed to load listings");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  const loadApps = useCallback(async () => {
    if (!user?.email) return;
    try {
      const userApps = await AppService.getUserApps(user.email);
      setApps(userApps);
    } catch (err) {
      console.error(err);
    }
  }, [user?.email]);

  useEffect(() => {
    if (user?.email) {
      loadListings();
      loadApps();
    }
  }, [user?.email, loadListings, loadApps]);

  useEffect(() => {
    if (!selectedApp || apps.length === 0) return;
    const app = apps.find((item) => item.appId === selectedApp);
    if (!app) return;

    setFormData((prev) => ({
      ...prev,
      category: app.category || prev.category,
    }));
  }, [apps, selectedApp]);

  const applyAppPrefill = (app: App) => {
    const ctx = app.researchContext;
    const prefillFeatures = toStringArray(ctx?.keyFeatures);
    const prefillUsps = toStringArray(ctx?.strategicRecommendations);
    const prefillKeywords = toStringArray(ctx?.growthSignals);

    setFormData((prev) => ({
      ...prev,
      appName: app.name || prev.appName,
      category: app.category || prev.category,
      platform: app.platform,
      targetAudience:
        ctx?.targetDemographics?.join(", ") ||
        app.description ||
        prev.targetAudience,
      features:
        prefillFeatures.length > 0
          ? [...prefillFeatures.slice(0, 3), "", "", ""].slice(0, 3)
          : prev.features,
      usp:
        prefillUsps.length > 0
          ? [...prefillUsps.slice(0, 2), "", ""].slice(0, 2)
          : prev.usp,
      keywords:
        prefillKeywords.length > 0
          ? [...prefillKeywords.slice(0, 3), "", "", ""].slice(0, 3)
          : prev.keywords,
    }));
  };

  const handleAppSelection = (appId: string) => {
    setSelectedApp(appId);
    const app = apps.find((a) => a.appId === appId);
    if (app) {
      applyAppPrefill(app);
    }
  };

  const getCreateTargetPlatforms = (): Array<"ios" | "android"> =>
    formData.platform === "both" ? ["ios", "android"] : [formData.platform];

  const validateCreateListingInputs = (): {
    app: App;
    payload: CreateListingDTO;
  } | null => {
    if (!user?.email || !formData.appName) {
      setError("Please fill required fields");
      return null;
    }
    if (formData.locales.length === 0) {
      setError("Select at least one locale before generating a listing.");
      return null;
    }
    if (
      formData.platform !== "android" &&
      !String(formData.supportUrl || "").trim()
    ) {
      setError("Support URL is required for iOS listing generation.");
      return null;
    }
    if (
      formData.platform !== "android" &&
      !String(formData.marketingUrl || "").trim()
    ) {
      setError("Marketing URL is required for iOS listing generation.");
      return null;
    }

    const app = apps.find((item) => item.appId === selectedApp);
    if (!app) {
      setError(
        "Select a registered app first. Privacy Policy and Terms links are required from App Registration.",
      );
      return null;
    }

    const missingFields: string[] = [];
    if (!String(app.privacyPolicyUrl || "").trim()) {
      missingFields.push("Privacy Policy URL");
    }
    if (!String(app.termsOfServiceUrl || "").trim()) {
      missingFields.push("Terms of Service URL");
    }
    if (missingFields.length > 0) {
      setError(
        `Missing required app links: ${missingFields.join(
          ", ",
        )}. Please update the selected app in App Registration before generating listing.`,
      );
      return null;
    }

    return {
      app,
      payload: {
        appName: formData.appName,
        category: formData.category,
        appDescription: app?.description || "",
        appPlatform: app?.platform,
        storeUrl: app?.storeUrl,
        androidStoreUrl: app?.androidStoreUrl,
        iosStoreUrl: app?.iosStoreUrl,
        privacyPolicyUrl: app?.privacyPolicyUrl,
        termsOfServiceUrl: app?.termsOfServiceUrl,
        supportUrl: String(formData.supportUrl || "").trim(),
        marketingUrl: String(formData.marketingUrl || "").trim(),
        researchContext: app?.researchContext,
        features: formData.features.filter((f) => f.trim()),
        targetAudience: formData.targetAudience,
        usps: formData.usp.filter((u) => u.trim()),
        keywords: formData.keywords.filter((k) => k.trim()),
        includeWhatsNew: effectiveIncludeWhatsNew,
        whatsNewBaseText: resolveWhatsNewBaseText(),
        listingContextBaseText: resolveListingContextBaseText(),
        model: createListingModel,
        platform: formData.platform,
        locales: formData.locales,
      },
    };
  };

  const handleGenerateBaseListingDraft = async () => {
    const validated = validateCreateListingInputs();
    if (!validated) return;

    const targetPlatforms = getCreateTargetPlatforms();

    try {
      setLoading(true);
      setCreateListingStage("generating_base");
      setCreateListingStatusIndex(0);
      setCreatedListingsResult([]);
      setError(null);
      setSuccess(null);

      const generatedDrafts: Partial<
        Record<"ios" | "android", IOSListingSchema | AndroidListingSchema>
      > = {};

      for (const platform of targetPlatforms) {
        generatedDrafts[platform] =
          await ListingService.generateBaseEnglishListingDraft(
            validated.payload,
            platform,
          );
      }

      setBaseListingDrafts(generatedDrafts);
      setBaseReviewPlatform(targetPlatforms[0]);
      setCreateListingStage("base_review");
    } catch (err) {
      console.error(err);
      setCreateListingStage("form");
      setError("Failed to generate base English listing draft.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateListing = async () => {
    const validated = validateCreateListingInputs();
    if (!validated || !user?.email) return;

    try {
      setLoading(true);
      setCreateListingStage("generating");
      setCreateListingStatusIndex(0);
      setCreatedListingsResult([]);
      setError(null);
      setSuccess(null);

      const createdListings = await ListingService.generateListing(
        user.email,
        selectedApp || undefined,
        validated.payload,
        {
          approvedBaseContentByPlatform: baseListingDrafts,
        },
      );
      await loadListings();
      const primaryCreatedListing =
        createdListings.find((item) => item.platform === formData.platform) ||
        createdListings[0] ||
        null;
      if (primaryCreatedListing) {
        setSelectedListing(primaryCreatedListing);
        setPreviewLocale(getListingLocales(primaryCreatedListing)[0] || "");
      }
      const skippedLocales = createdListings.flatMap(
        (listing) => listing.generationSkippedLocales || [],
      );
      setCreatedListingsResult(createdListings);
      setCreateListingStage("success");
      setError(null);
      setSuccess(
        skippedLocales.length > 0
          ? `Listing generation completed with ${skippedLocales.length} skipped locale${
              skippedLocales.length === 1 ? "" : "s"
            }.`
          : "Listing generation completed.",
      );
    } catch (err) {
      setCreateListingStage("base_review");
      setError("Failed to create listing");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const exportVariants = (format: "text" | "json") => {
    if (!selectedListing) return;
    const selectedEntries = Array.isArray(selectedListing.entries)
      ? selectedListing.entries
      : [];
    const content =
      format === "json"
        ? ListingService.exportAllEntriesAsJSON(selectedListing)
        : selectedEntries
            .map(
              (entry) =>
                `Locale: ${entry.locale}\n${ListingService.exportEntryAsText(
                  selectedListing,
                  entry.locale,
                )}`,
            )
            .join("\n\n");

    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:" +
        (format === "json" ? "application/json" : "text/plain") +
        ";charset=utf-8," +
        encodeURIComponent(content),
    );
    element.setAttribute(
      "download",
      `${selectedListing.appName}-listing.${
        format === "json" ? "json" : "txt"
      }`,
    );
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleDeleteListing = async (listingId: string) => {
    if (window.confirm("Delete this listing?")) {
      try {
        await ListingService.deleteListing(listingId);
        if (selectedListing?.listingId === listingId) {
          setSelectedListing(null);
          setPreviewLocale("");
          setEditableEntryContent(null);
        }
        await loadListings();
      } catch (err) {
        setError("Failed to delete listing");
      }
    }
  };

  const openPublishDialogForListing = (listing: AppStoreListingType) => {
    const app = getAppForListing(listing);
    const publishableLocales =
      listing.platform === "ios"
        ? getListingEntries(listing)
            .filter(
              (entry) =>
                typeof entry?.locale === "string" &&
                entry.locale.trim().length > 0 &&
                "subtitle" in
                  (entry.content as IOSListingSchema | AndroidListingSchema) &&
                Boolean(getStoreLocaleOption(entry.locale)?.supportsAppStore),
            )
            .map((entry) => entry.locale)
        : getPublishableAndroidEntries(listing)
            .map((entry) => entry.locale)
            .filter((locale) => {
              const option = getStoreLocaleOption(locale);
              return (
                GOOGLE_PLAY_LOCALE_REGEX.test(locale) &&
                Boolean(option?.supportsGooglePlay)
              );
            });
    const preferredLocale = String(app?.googlePlayDefaultLocale || "").trim();
    const nextLocale =
      (preferredLocale && publishableLocales.includes(preferredLocale)
        ? preferredLocale
        : publishableLocales[0]) || "";

    setPublishTargetListing(listing);
    setPublishMode("all_locales");
    setPublishLocaleFilter("supported");
    setPublishLocale(nextLocale);
    setPublishSelectedLocales(nextLocale ? [nextLocale] : []);
    setPublishResult(null);
    setValidationResult(null);
    setPublishConfirmationChecked(false);
    setPublishIgnoreValidationIssues(false);
    setPublishSkipValidationOverride(false);
    setValidationPreviewLayout("stacked");
    setPublishSections({
      overview: true,
      target: true,
      validate: true,
      preview: false,
      confirm: true,
      result: false,
    });
    setPublishDialogOpen(true);
  };

  const closePublishDialog = () => {
    if (publishingToGooglePlay || validatingGooglePlay) return;
    setPublishDialogOpen(false);
    setPublishTargetListing(null);
    setPublishMode("all_locales");
    setPublishLocaleFilter("supported");
    setPublishLocale("");
    setPublishSelectedLocales([]);
    setPublishResult(null);
    setValidationResult(null);
    setPublishConfirmationChecked(false);
    setPublishIgnoreValidationIssues(false);
    setPublishSkipValidationOverride(false);
    setValidationPreviewLayout("stacked");
    setPublishSections({
      overview: true,
      target: true,
      validate: true,
      preview: false,
      confirm: true,
      result: false,
    });
  };

  const handleFetchWhatsNew = async () => {
    if (!whatsNewAppId) {
      setWhatsNewError("Please select an app.");
      return;
    }
    try {
      setWhatsNewFetching(true);
      setWhatsNewError(null);
      setWhatsNewSuccess(null);
      setWhatsNewPushResult(null);
      const result = await ListingService.fetchAppStoreConnectWhatsNew({
        appId: whatsNewAppId,
      });
      setWhatsNewFetchResult(result);
      // Pre-populate translations with the existing values from ASC
      const initial: Record<string, string> = {};
      for (const item of result.locales) {
        initial[item.locale] = item.whatsNew;
      }
      setWhatsNewTranslations(initial);
      setWhatsNewBaseText("");
      setWhatsNewSuccess(
        result.message ||
          `Fetched ${result.locales.length} locales from App Store Connect.`,
      );
    } catch (err: any) {
      setWhatsNewError(
        err?.responseData?.error ||
          err?.message ||
          "Failed to fetch current listing.",
      );
    } finally {
      setWhatsNewFetching(false);
    }
  };

  const handleTranslateWhatsNew = async () => {
    const baseText = whatsNewBaseText.trim();
    if (!baseText) {
      setWhatsNewError("Enter the What's New text to translate.");
      return;
    }
    if (!whatsNewFetchResult?.locales?.length) {
      setWhatsNewError("Fetch the current listing first.");
      return;
    }
    try {
      setWhatsNewTranslating(true);
      setWhatsNewError(null);
      const locales = whatsNewFetchResult.locales.map((item) => item.locale);
      const updated = { ...whatsNewTranslations };

      for (const locale of locales) {
        if (
          locale === "en-US" ||
          locale === "en-GB" ||
          locale.startsWith("en")
        ) {
          updated[locale] = baseText;
          continue;
        }
        const localeOption = getStoreLocaleOption(locale);
        const langName = localeOption?.name || locale;
        try {
          const response = await AIService.generateText(baseText, {
            systemInstruction: `Translate the following App Store "What's New" release notes into ${langName}. Keep the tone natural and concise. Return only the translated text, no extra commentary.`,
            model: "gemini-2.5-flash-lite",
          });
          updated[locale] = response.text.trim();
        } catch {
          // keep existing value on individual locale failure
        }
      }
      setWhatsNewTranslations(updated);
      setWhatsNewSuccess(
        "Translations generated. Review and edit before pushing.",
      );
    } catch (err: any) {
      setWhatsNewError(err?.message || "Translation failed.");
    } finally {
      setWhatsNewTranslating(false);
    }
  };

  const handlePushWhatsNew = async () => {
    if (!user?.email) {
      setWhatsNewError("Please sign in again.");
      return;
    }
    if (!whatsNewAppId) {
      setWhatsNewError("Please select an app.");
      return;
    }
    const entries = Object.entries(whatsNewTranslations).filter(([, v]) =>
      v.trim(),
    );
    if (entries.length === 0) {
      setWhatsNewError("No What's New content to push.");
      return;
    }
    try {
      setWhatsNewPushing(true);
      setWhatsNewError(null);
      setWhatsNewSuccess(null);
      const payload = Object.fromEntries(entries);
      const result = await ListingService.updateAppStoreConnectWhatsNew({
        appId: whatsNewAppId,
        whatsNewByLocale: payload,
      });
      setWhatsNewPushResult(result);
      if (result.success) {
        const selectedAppRecord = apps.find(
          (app) => app.appId === whatsNewAppId,
        );
        if (!selectedAppRecord) {
          setWhatsNewSuccess(
            result.message || "What's New updated on App Store Connect.",
          );
          return;
        }

        const snapshot = await ListingService.createIOSWhatsNewSnapshot({
          userId: user.email,
          appId: whatsNewAppId,
          appName: selectedAppRecord.name,
          category: selectedAppRecord.category,
          whatsNewByLocale: payload,
        });

        setListings((prev) => [snapshot, ...prev]);
        setSelectedListing(snapshot);
        setPreviewLocale(getListingLocales(snapshot)[0] || "");
        setListingFilterAppId(whatsNewAppId);
        setTabValue(2);
        setWhatsNewSuccess(
          `${result.message || "What's New updated on App Store Connect."} Saved as a new listing snapshot in Listing Preview.`,
        );
      } else {
        setWhatsNewError(result.message || "Some locales failed to update.");
      }
    } catch (err: any) {
      setWhatsNewError(
        err?.responseData?.error ||
          err?.message ||
          "Failed to push What's New.",
      );
    } finally {
      setWhatsNewPushing(false);
    }
  };

  const handleValidateGooglePlayListing = async () => {
    if (!publishTargetListing?.appId) {
      setError("Selected listing is not linked to a registered app.");
      return;
    }

    try {
      setValidatingGooglePlay(true);
      setError(null);
      setSuccess(null);
      setPublishResult(null);

      const request = {
        appId: publishTargetListing.appId,
        listingId: publishTargetListing.listingId,
        mode: publishMode,
        ...(publishMode === "single_locale" ? { locale: publishLocale } : {}),
        ...(publishMode === "selected_locales"
          ? { locales: publishSelectedLocales }
          : {}),
      };
      const response =
        publishTargetListing.platform === "ios"
          ? await ListingService.validateAppStoreConnectListing(request)
          : await ListingService.validateGooglePlayListing(request);

      setValidationResult(response);
      setPublishSkipValidationOverride(false);
      setSuccess(
        response.message ||
          `Fetched current ${publishTargetListing.platform === "ios" ? "App Store Connect" : "Google Play"} listing details for validation.`,
      );
    } catch (validationError: any) {
      console.error(validationError);
      const responseData = validationError?.responseData as
        | ListingValidationResponse
        | undefined;
      if (responseData?.packageName) {
        setValidationResult(responseData);
      }
      setError(
        responseData?.message ||
          validationError?.message ||
          `Failed to validate ${publishTargetListing.platform === "ios" ? "App Store Connect" : "Google Play"} listing.`,
      );
    } finally {
      setValidatingGooglePlay(false);
    }
  };

  const handlePublishToGooglePlay = async () => {
    if (!publishTargetListing?.appId) {
      setError("Selected listing is not linked to a registered app.");
      return;
    }

    try {
      setPublishingToGooglePlay(true);
      setError(null);
      setSuccess(null);
      setPublishResult(null);

      const request = {
        appId: publishTargetListing.appId,
        listingId: publishTargetListing.listingId,
        mode: publishMode,
        ...(publishMode === "single_locale" ? { locale: publishLocale } : {}),
        ...(publishMode === "selected_locales"
          ? { locales: publishSelectedLocales }
          : {}),
      };
      const response =
        publishTargetListing.platform === "ios"
          ? await ListingService.publishAppStoreConnectListing(request)
          : await ListingService.publishGooglePlayListing(request);

      if (response.commitStatus === "committed") {
        const updatedListing = await ListingService.markListingPublished({
          listingId: publishTargetListing.listingId,
          publishedAt: response.lastPublishedAt
            ? new Date(response.lastPublishedAt)
            : new Date(),
          platform: publishTargetListing.platform,
        });
        setListings((prev) =>
          prev.map((item) =>
            item.listingId === updatedListing.listingId ? updatedListing : item,
          ),
        );
        setSelectedListing((prev) =>
          prev?.listingId === updatedListing.listingId ? updatedListing : prev,
        );
      }

      if (response.lastPublishedAt) {
        const lastPublishedAt = new Date(response.lastPublishedAt);
        setApps((prev) =>
          prev.map((app) =>
            app.appId === publishTargetListing.appId
              ? {
                  ...app,
                  ...(publishTargetListing.platform === "android"
                    ? { googlePlayLastPublishedAt: lastPublishedAt }
                    : { appStoreConnectLastPublishedAt: lastPublishedAt }),
                }
              : app,
          ),
        );
      }
      setPublishResult(response);
      setSuccess(
        response.message ||
          `${publishTargetListing.platform === "ios" ? "App Store Connect" : "Google Play"} listing published.`,
      );
    } catch (publishError: any) {
      console.error(publishError);
      const responseData = publishError?.responseData as
        | ListingPublishResponse
        | undefined;
      if (responseData?.packageName) {
        setPublishResult(responseData);
      }
      setError(
        publishError?.message ||
          `Failed to publish listing to ${publishTargetListing.platform === "ios" ? "App Store Connect" : "Google Play"}.`,
      );
    } finally {
      setPublishingToGooglePlay(false);
    }
  };

  const handleEditableFieldChange = (
    fieldKey: EditableListingFieldKey,
    value: string,
  ) => {
    if (!selectedListing || !activePreviewContent) return;
    if (previewLocale) {
      setEnglishFieldTranslations((prev) => {
        const next = { ...prev };
        delete next[`${previewLocale}:${fieldKey}`];
        return next;
      });
    }
    setEditableEntryContent(
      buildUpdatedEditableContent(
        activePreviewContent,
        fieldKey,
        value,
        selectedListing.platform,
      ),
    );
  };

  const handleResetPreviewEdits = () => {
    if (!savedPreviewContent) return;
    setEditableEntryContent(savedPreviewContent);
  };

  const handlePublishSectionToggle =
    (section: PublishSectionKey) =>
    (_event: React.SyntheticEvent, expanded: boolean) => {
      setPublishSections((prev) => ({
        ...prev,
        [section]: expanded,
      }));
    };

  const renderValidationFieldComparison = ({
    label,
    current,
    proposed,
    limit,
    multiline = false,
  }: {
    label: string;
    current: string;
    proposed: string;
    limit: number;
    multiline?: boolean;
  }) => {
    const currentLength = String(current || "").length;
    const proposedLength = String(proposed || "").length;
    const textSx = multiline
      ? {
          whiteSpace: "pre-wrap" as const,
          maxHeight: 120,
          overflow: "auto" as const,
        }
      : undefined;

    return (
      <Paper
        variant="outlined"
        sx={{ p: 1.25, borderRadius: 1.75, bgcolor: "grey.50" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: 0.5,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Current {currentLength} / Proposed {proposedLength} / Limit {limit}{" "}
            chars
          </Typography>
        </Box>

        {validationPreviewLayout === "side_by_side" ? (
          <Grid container spacing={1.25}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Current
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {currentLength} chars
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75, ...textSx }}>
                  {current || "Not set"}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  borderColor: "primary.light",
                  bgcolor: "rgba(25, 118, 210, 0.04)",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Proposed
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {proposedLength} chars
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 0.75, fontWeight: 700, ...textSx }}
                >
                  {proposed || "Not set"}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <>
            <Typography variant="body2" sx={{ mt: 0.5, ...textSx }}>
              Current: {current || "Not set"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, mt: 0.5, ...textSx }}
            >
              Proposed: {proposed || "Not set"}
            </Typography>
          </>
        )}
      </Paper>
    );
  };

  const handleSavePreviewEdits = async () => {
    if (!selectedListing || !previewEntry || !activePreviewContent) return;

    try {
      setSavingEntryContent(true);
      setError(null);
      setSuccess(null);

      const updatedListing = await ListingService.updateListingEntryContent({
        listingId: selectedListing.listingId,
        locale: previewEntry.locale,
        platform: selectedListing.platform,
        content: activePreviewContent,
      });

      setListings((prev) =>
        prev.map((item) =>
          item.listingId === updatedListing.listingId ? updatedListing : item,
        ),
      );
      setSelectedListing(updatedListing);
      const refreshedEntry = getPreviewEntry(
        updatedListing,
        previewEntry.locale,
      );
      if (refreshedEntry) {
        setEditableEntryContent(
          ListingService.sanitizeListingContentForPlatform(
            refreshedEntry.content as IOSListingSchema | AndroidListingSchema,
            updatedListing.platform,
          ) as IOSListingSchema | AndroidListingSchema,
        );
      }
      setSuccess(
        `Saved ${getLocaleDisplayLabel(previewEntry.locale)} listing edits.`,
      );
    } catch (saveError) {
      console.error(saveError);
      setError("Failed to save listing edits.");
    } finally {
      setSavingEntryContent(false);
    }
  };

  const generateOptimizedListingFieldValue = async (params: {
    listing: AppStoreListingType;
    locale: string;
    field: EditableListingFieldConfig;
    content: IOSListingSchema | AndroidListingSchema;
    issue?: {
      label: string;
      count: number;
      limit: number;
      overflow: number;
    };
  }) => {
    const app = getAppForListing(params.listing);
    const currentValue = getEditableFieldValue(
      params.content,
      params.field.key,
    );
    const localeLabel = getLocaleDisplayLabel(params.locale);
    const getFieldMeasure = (value: string) =>
      params.field.key === "keywords"
        ? getKeywordsCharacterCount(value)
        : value.trim().length;
    const getFieldLimit = () =>
      params.field.maxLength ||
      (params.field.key === "keywords"
        ? APP_STORE_KEYWORDS_MAX_CHARS
        : undefined);
    const isCompliantValue = (value: string) => {
      const limit = getFieldLimit();
      return !limit || getFieldMeasure(value) <= limit;
    };
    const extractFinalOptimizedValue = (rawResponse: string) => {
      const normalized = String(rawResponse || "").trim();
      const finalMatch = normalized.match(
        /Final Optimized Result:\s*([\s\S]+)$/i,
      );
      if (finalMatch?.[1]) {
        return finalMatch[1].trim();
      }
      return normalized;
    };

    let lastCandidate = currentValue;

    for (let attempt = 1; attempt <= (params.issue ? 3 : 1); attempt += 1) {
      const constraintLines = [
        `Field: ${params.field.aiTaskLabel}`,
        `Locale: ${params.locale} (${localeLabel})`,
        `Platform: ${getPlatformLabel(params.listing.platform)}`,
        params.field.maxLength
          ? `Maximum characters: ${params.field.maxLength}`
          : "Maximum: 100 total characters across comma-separated keywords",
        "Prioritize clarity, high-impact keywords, and conversion intent.",
        "Use modern, natural-sounding language for the target locale and region.",
        "Use straight quotes and standard punctuation only.",
        "Avoid curly quotes, em dashes, ellipses, hidden unicode, HTML, and bracket-heavy formatting.",
        params.field.key === "keywords"
          ? "Return only a comma-separated keyword list that stays within 100 total characters."
          : "Keep the final optimized result as the only user-facing store field value.",
        ...(params.issue
          ? [
              "Hard requirement: the response must fit within the exact store limit.",
              "If needed, rewrite from scratch instead of lightly editing the current copy.",
              "Prefer shorter, sharper wording over descriptive filler.",
              "Aim to land slightly under the limit for safety rather than exactly on it.",
            ]
          : []),
      ];

      const prompt = [
        "Role: You are an expert App Store Optimization (ASO) copywriter and localizer.",
        `Task: Rewrite and optimize one ${getPlatformLabel(
          params.listing.platform,
        )} listing field for ${localeLabel}.`,
        ...buildAppContextLines(
          app || {
            name: params.listing.appName,
            category: params.listing.category,
            platform: params.listing.platform,
          },
        ),
        `Selected locale: ${params.locale}`,
        `Current field value: ${currentValue || "N/A"}`,
        ...(attempt > 1 && params.issue
          ? [
              "",
              "Previous attempt failed the hard limit:",
              `- Previous output: ${lastCandidate || "N/A"}`,
              `- Actual length/count: ${getFieldMeasure(lastCandidate)}`,
              `- Allowed maximum: ${getFieldLimit()}`,
              "- Make this retry noticeably shorter while preserving the core meaning.",
            ]
          : []),
        "",
        "Optimization goals:",
        "- Maximize conversion rate and listing clarity.",
        "- Preserve the app's real positioning and core value proposition.",
        "- Respect the store-specific character limits exactly.",
        "- Keep the response natural for the target locale.",
        ...(params.issue
          ? ["- Resolve the active listing guideline issue cleanly."]
          : []),
        "",
        ...buildGuidelineIssuePromptLines(params.issue),
        ...(params.issue ? [""] : []),
        "Verification Step (Mandatory):",
        "- Draft the optimized text.",
        "- Calculate its exact character count before finalizing.",
        "- If it is over the limit, rewrite it shorter before giving the final optimized result.",
        "",
        `Source Text: "${currentValue || "N/A"}"`,
        "",
        "Constraints:",
        ...constraintLines.map((line) => `- ${line}`),
        "",
        "Format your response exactly as:",
        "Draft: [your draft]",
        `Character Count: [${params.field.key === "keywords" ? "total characters" : "number"}]`,
        "Final Optimized Result: [the final compliant result]",
      ].join("\n");

      const response = await AIService.generateText(prompt, {
        model: listingAiModel,
        temperature: params.issue ? 0.2 : 0.4,
        maxTokens: params.field.key === "keywords" ? 200 : 800,
      });

      const rawText = extractFinalOptimizedValue(String(response.text || ""))
        .trim()
        .replace(/^["']|["']$/g, "");
      const nextValue =
        params.field.key === "keywords"
          ? parseKeywordsValue(rawText).join(", ")
          : rawText;

      lastCandidate = nextValue;

      if (isCompliantValue(nextValue)) {
        return {
          model: response.model,
          nextValue,
        };
      }
    }

    throw new Error(
      `AI could not produce a compliant ${params.field.label} within the store limit.`,
    );
  };

  const handleOptimizeListingField = async (
    field: EditableListingFieldConfig,
  ) => {
    if (!selectedListing || !previewEntry || !activePreviewContent) return;

    const fieldIssue = getContentGuidelineIssues(
      selectedListing.platform,
      activePreviewContent,
    ).find((issue) => issue.key === field.key);

    try {
      setOptimizingFieldKey(field.key);
      setError(null);
      setSuccess(null);

      const response = await generateOptimizedListingFieldValue({
        listing: selectedListing,
        locale: previewEntry.locale,
        field,
        content: activePreviewContent,
        issue: fieldIssue,
      });

      handleEditableFieldChange(field.key, response.nextValue);
      setSuccess(
        `${
          fieldIssue ? "Fixed" : "Optimized"
        } ${field.label} with ${response.model}. Review and save when ready.`,
      );
    } catch (optimizeError) {
      console.error(optimizeError);
      setError(`Failed to optimize ${field.label}.`);
    } finally {
      setOptimizingFieldKey(null);
    }
  };

  const handleTranslateFieldToEnglish = async (
    field: EditableListingFieldConfig,
  ) => {
    if (!selectedListing || !previewEntry || !activePreviewContent) return;

    const currentValue = getEditableFieldValue(
      activePreviewContent,
      field.key,
    ).trim();
    if (!currentValue) return;

    const fieldTranslationKey = `${previewEntry.locale}:${field.key}`;
    const localeLabel = getLocaleDisplayLabel(previewEntry.locale);
    const isKeywordsField = field.key === "keywords";

    const prompt = [
      "Translate the following mobile app store listing field into natural, clear English for reviewer comprehension.",
      "Preserve the original meaning and intent.",
      "Do not optimize, shorten, expand, or rewrite for conversion.",
      "Return plain text only with no labels, quotes, markdown, bullets, emojis, or commentary.",
      isKeywordsField
        ? "If the input is a keyword list, return the translated English keywords as a comma-separated list."
        : "Return only the translated English field value.",
      `Source locale: ${previewEntry.locale} (${localeLabel})`,
      `Platform: ${getPlatformLabel(selectedListing.platform)}`,
      `Field: ${field.aiTaskLabel}`,
      "",
      currentValue,
    ].join("\n");

    try {
      setTranslatingFieldKey(fieldTranslationKey);
      setError(null);
      setSuccess(null);

      const response = await AIService.generateText(prompt, {
        model: listingAiModel,
        temperature: 0.1,
        maxTokens: isKeywordsField ? 220 : 900,
      });

      const translatedText = String(response.text || "")
        .trim()
        .replace(/^["']|["']$/g, "");

      setEnglishFieldTranslations((prev) => ({
        ...prev,
        [fieldTranslationKey]: {
          sourceValue: currentValue,
          translatedText,
          model: response.model,
        },
      }));
      setSuccess(
        `Translated ${field.label} to English with ${response.model}.`,
      );
    } catch (translateError) {
      console.error(translateError);
      setError(`Failed to translate ${field.label} to English.`);
    } finally {
      setTranslatingFieldKey(null);
    }
  };

  const handleCopyText = async (value: string, label: string) => {
    const text = String(value || "").trim();
    if (!text) return;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setSuccess(`${label} copied.`);
      setError(null);
      setCopiedField(label);
      window.setTimeout(() => {
        setCopiedField((prev) => (prev === label ? null : prev));
      }, 2200);
    } catch (copyError) {
      console.error(copyError);
      setError(`Failed to copy ${label.toLowerCase()}.`);
    }
  };

  const formatKeywordsForCopy = (keywords: string[]): string => {
    return sanitizeDisplayKeywords(keywords)
      .slice(0, 10)
      .map((keyword) => keyword.replace(/\s+/g, "").trim())
      .filter(Boolean)
      .join(",");
  };

  const sanitizeDisplayText = (value: string, preserveLineBreaks = false) => {
    let sanitized = String(value || "")
      .replace(/\r\n?/g, "\n")
      .replace(/[\u00A0\u1680\u2000-\u200B\u202F\u205F\u3000]/g, " ")
      .replace(/[\u200C\u200D\u2060\uFE0E\uFE0F\uFEFF]/g, "")
      .replace(/[“”«»„‟]/g, '"')
      .replace(/[‘’‚‛‹›]/g, "'")
      .replace(/[‐‑‒–—―]/g, "-")
      .replace(/[•◦▪▫●○◆◇▶▷]/g, "-")
      .replace(/…/g, "...")
      .replace(/[×÷≈≠≤≥±√∑∏∞]/g, " ")
      .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, "")
      .replace(/\t/g, " ");

    if (!preserveLineBreaks) {
      sanitized = sanitized.replace(/\n+/g, " ");
    } else {
      sanitized = sanitized.replace(/ *\n */g, "\n").replace(/\n{3,}/g, "\n\n");
    }

    return sanitized.replace(/[ ]{2,}/g, " ").trim();
  };

  const sanitizeDisplayKeywords = (keywords: unknown): string[] => {
    return toStringArray(keywords)
      .map((keyword) =>
        sanitizeDisplayText(keyword)
          .replace(/\+/g, " plus ")
          .replace(/[{}()<>]/g, " ")
          .replaceAll("[", " ")
          .replaceAll("]", " ")
          .replace(/\s+/g, " ")
          .trim(),
      )
      .filter(Boolean);
  };

  const getDisplayListingContent = (
    content: IOSListingSchema | AndroidListingSchema,
  ): IOSListingSchema | AndroidListingSchema => {
    if ("subtitle" in content) {
      return {
        ...content,
        appName: sanitizeDisplayText(content.appName),
        title: sanitizeDisplayText(content.title),
        subtitle: sanitizeDisplayText(content.subtitle),
        promotionalText: sanitizeDisplayText(content.promotionalText),
        supportUrl: sanitizeDisplayText(content.supportUrl || ""),
        marketingUrl: sanitizeDisplayText(content.marketingUrl || ""),
        whatsNew: sanitizeDisplayText(content.whatsNew || "", true),
        description: sanitizeDisplayText(content.description, true),
        keywords: sanitizeDisplayKeywords(content.keywords),
      };
    }

    return {
      ...content,
      appName: sanitizeDisplayText(content.appName),
      title: sanitizeDisplayText(content.title),
      shortDescription: sanitizeDisplayText(content.shortDescription),
      fullDescription: sanitizeDisplayText(content.fullDescription, true),
    };
  };

  const handleLocaleToggle = (code: string) => {
    setFormData((prev) => ({
      ...prev,
      locales: prev.locales.includes(code)
        ? prev.locales.filter((locale) => locale !== code)
        : [...prev.locales, code],
    }));
  };

  const getAppGroupingKey = (listing: AppStoreListingType) =>
    listing.appId || `manual-${listing.appName.toLowerCase()}`;

  const getListingCreatedAt = (listing: AppStoreListingType) => {
    const parsed =
      listing.createdAt instanceof Date
        ? listing.createdAt
        : new Date(listing.createdAt);
    return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
  };

  const getListingPublishedAt = (listing: AppStoreListingType) => {
    if (!listing.googlePlayPublishedAt) return null;
    const parsed =
      listing.googlePlayPublishedAt instanceof Date
        ? listing.googlePlayPublishedAt
        : new Date(listing.googlePlayPublishedAt);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatListingDateLabel = (value: Date) =>
    value.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const getListingVersionLabel = (listing: AppStoreListingType) =>
    listing.versionNumber
      ? `Version ${listing.versionNumber}`
      : "Legacy version";

  const groupedListings = listings.reduce<
    Record<string, { appName: string; items: AppStoreListingType[] }>
  >((acc, listing) => {
    const key = getAppGroupingKey(listing);
    if (!acc[key]) {
      acc[key] = {
        appName: listing.appName,
        items: [],
      };
    }
    acc[key].items.push(listing);
    return acc;
  }, {});

  const filteredGroupedListings = Object.entries(groupedListings)
    .map(([key, group]) => {
      const filteredItems = group.items
        .filter((item) => item.appId === listingFilterAppId)
        .sort((left, right) => {
          const versionDiff =
            Number(right.versionNumber || 0) - Number(left.versionNumber || 0);
          if (versionDiff !== 0) return versionDiff;
          return (
            getListingCreatedAt(right).getTime() -
            getListingCreatedAt(left).getTime()
          );
        });

      const dateGroups = filteredItems.reduce<
        Array<{ key: string; label: string; items: AppStoreListingType[] }>
      >((acc, item) => {
        const createdAt = getListingCreatedAt(item);
        const dateKey = createdAt.toISOString().slice(0, 10);
        const existingGroup = acc.find((entry) => entry.key === dateKey);
        if (existingGroup) {
          existingGroup.items.push(item);
          return acc;
        }
        acc.push({
          key: dateKey,
          label: formatListingDateLabel(createdAt),
          items: [item],
        });
        return acc;
      }, []);

      return {
        key,
        appName: group.appName,
        items: filteredItems,
        dateGroups,
      };
    })
    .filter((group) => group.items.length > 0);

  const getPreviewDateGroupKey = (groupKey: string, dateKey: string) =>
    `${groupKey}:${dateKey}`;

  const getListingsDateGroupKey = (groupKey: string, dateKey: string) =>
    `listings:${groupKey}:${dateKey}`;

  const isLatestDateGroup = (
    group: { dateGroups: Array<{ key: string }> },
    dateKey: string,
  ) => group.dateGroups[0]?.key === dateKey;

  const getPlatformLabel = (platform: string) => {
    if (platform === "ios") return "Apple App Store";
    if (platform === "android") return "Google Play";
    return platform || "Unknown Platform";
  };

  const getPlatformIcon = (platform: string) => {
    if (platform === "ios") {
      return <AppleIcon sx={{ fontSize: 16 }} />;
    }
    if (platform === "android") {
      return <AndroidIcon sx={{ fontSize: 16 }} />;
    }
    return null;
  };

  const getListingLocales = (listing: AppStoreListingType) =>
    Array.isArray((listing as any).locales) ? (listing as any).locales : [];

  const getListingEntries = (
    listing: AppStoreListingType,
  ): ListingLocaleEntry[] =>
    Array.isArray((listing as any).entries)
      ? ((listing as any).entries as ListingLocaleEntry[])
      : [];

  const getPreviewEntry = (
    listing: AppStoreListingType | null,
    locale: string,
  ): ListingLocaleEntry | null => {
    if (!listing) return null;
    return (
      getListingEntries(listing).find((item) => item.locale === locale) ||
      getListingEntries(listing)[0] ||
      null
    );
  };

  const isAndroidListingContent = (
    content: IOSListingSchema | AndroidListingSchema,
  ): content is AndroidListingSchema =>
    Boolean(
      content &&
      typeof (content as AndroidListingSchema).title === "string" &&
      typeof (content as AndroidListingSchema).shortDescription === "string" &&
      typeof (content as AndroidListingSchema).fullDescription === "string",
    );

  const getAppForListing = (listing: AppStoreListingType | null) =>
    apps.find((app) => app.appId === listing?.appId) || null;

  const isCurrentPlayVersion = (listing: AppStoreListingType) => {
    if (listing.platform !== "android" || !listing.appId) {
      return false;
    }

    const publishedAt = getListingPublishedAt(listing);
    if (!publishedAt) {
      return false;
    }

    return (
      listings
        .filter(
          (item) =>
            item.appId === listing.appId &&
            item.platform === "android" &&
            Boolean(getListingPublishedAt(item)),
        )
        .sort((left, right) => {
          const publishedDiff =
            (getListingPublishedAt(right)?.getTime() || 0) -
            (getListingPublishedAt(left)?.getTime() || 0);
          if (publishedDiff !== 0) return publishedDiff;
          return (
            Number(right.versionNumber || 0) - Number(left.versionNumber || 0)
          );
        })[0]?.listingId === listing.listingId
    );
  };

  const formatLastPublishedDate = (value?: Date | string | null) => {
    if (!value) return null;
    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getPublishableAndroidEntries = (listing: AppStoreListingType | null) =>
    listing && listing.platform === "android"
      ? getListingEntries(listing).filter(
          (entry) =>
            typeof entry?.locale === "string" &&
            entry.locale.trim().length > 0 &&
            isAndroidListingContent(
              entry.content as IOSListingSchema | AndroidListingSchema,
            ),
        )
      : [];

  const getGooglePlayPublishValidation = (
    listing: AppStoreListingType | null,
  ) => {
    const blockers: string[] = [];
    const warnings: string[] = [];
    const app = getAppForListing(listing);
    const packageName = String(app?.googlePlayPackageName || "").trim();
    const publishableEntries = getPublishableAndroidEntries(listing);
    const generatedLocales = publishableEntries.map((entry) => entry.locale);
    const invalidFormatLocales: string[] = [];
    const unsupportedLocales: string[] = [];
    const publishableLocales = generatedLocales.filter((locale) => {
      const localeOption = getStoreLocaleOption(locale);
      const isRegexValid = GOOGLE_PLAY_LOCALE_REGEX.test(locale);
      const isGooglePlaySupported = Boolean(localeOption?.supportsGooglePlay);
      if (!isRegexValid) {
        invalidFormatLocales.push(locale);
        return false;
      }
      if (!isGooglePlaySupported) {
        unsupportedLocales.push(locale);
        return false;
      }
      return true;
    });
    const skippedLocales = [...invalidFormatLocales, ...unsupportedLocales];

    if (!listing) {
      blockers.push("Choose an Android listing to publish.");
    } else if (listing.platform !== "android") {
      blockers.push(
        "Google Play publishing is only available for Android listings.",
      );
    }

    if (!app) {
      blockers.push("This listing is not linked to a registered app.");
    }

    if (!packageName) {
      blockers.push(
        "Add a Google Play package name in App Registration before publishing.",
      );
    }

    if (publishableEntries.length === 0) {
      blockers.push(
        "This listing does not contain any publishable Android locale entries.",
      );
    }

    if (publishableLocales.length === 0 && skippedLocales.length > 0) {
      blockers.push(
        "This listing does not contain any Google Play-supported locale entries.",
      );
    }

    if (skippedLocales.length > 0) {
      if (unsupportedLocales.length > 0) {
        warnings.push(
          `Locales not supported by Google Play will be skipped: ${unsupportedLocales.join(", ")}.`,
        );
      }
      if (invalidFormatLocales.length > 0) {
        warnings.push(
          `Locales with invalid Google Play format will be skipped: ${invalidFormatLocales.join(", ")}.`,
        );
      }
    }

    return {
      app,
      packageName,
      publishableEntries,
      generatedLocales,
      publishableLocales,
      skippedLocales,
      blockers,
      warnings,
    };
  };

  const getAppStoreConnectPublishValidation = (
    listing: AppStoreListingType | null,
  ) => {
    const blockers: string[] = [];
    const warnings: string[] = [];
    const app = getAppForListing(listing);
    const bundleId = String(app?.storeId || "").trim();
    const publishableEntries =
      listing && listing.platform === "ios"
        ? getListingEntries(listing).filter(
            (entry) =>
              typeof entry?.locale === "string" &&
              entry.locale.trim().length > 0 &&
              "subtitle" in
                (entry.content as IOSListingSchema | AndroidListingSchema),
          )
        : [];
    const generatedLocales = publishableEntries.map((entry) => entry.locale);
    const skippedLocales: string[] = [];
    const publishableLocales = generatedLocales.filter((locale) => {
      const localeOption = getStoreLocaleOption(locale);
      const isSupported = Boolean(localeOption?.supportsAppStore);
      if (!isSupported) {
        skippedLocales.push(locale);
      }
      return isSupported;
    });

    if (!listing) {
      blockers.push("Choose an iOS listing to publish.");
    } else if (listing.platform !== "ios") {
      blockers.push(
        "App Store Connect publishing is only available for iOS listings.",
      );
    }

    if (!app) {
      blockers.push("This listing is not linked to a registered app.");
    }

    if (!bundleId && !String(app?.iosStoreUrl || "").trim()) {
      blockers.push(
        "Add the app bundle ID in App Registration store ID, or provide an iOS Store URL before publishing.",
      );
    }

    if (!String(app?.privacyPolicyUrl || "").trim()) {
      warnings.push(
        "Privacy Policy URL is missing in App Registration. Title and subtitle can publish, but privacy policy metadata will be skipped.",
      );
    }

    if (publishableEntries.length === 0) {
      blockers.push(
        "This listing does not contain any publishable iOS locale entries.",
      );
    }
    if (publishableLocales.length === 0 && skippedLocales.length > 0) {
      blockers.push(
        "This listing does not contain any App Store Connect-supported locale entries.",
      );
    }
    if (skippedLocales.length > 0) {
      warnings.push(
        `Locales not supported by App Store Connect will be skipped: ${skippedLocales.join(", ")}.`,
      );
    }

    return {
      app,
      packageName:
        bundleId || String(app?.iosStoreUrl || "").trim() || "Not configured",
      publishableEntries,
      generatedLocales,
      publishableLocales,
      skippedLocales,
      blockers,
      warnings,
    };
  };

  const getLocaleDisplayLabel = (localeCode: string) => {
    return getStoreLocaleDisplayLabel(localeCode);
  };

  const focusLocaleIssueEditor = useCallback((locale: string) => {
    setPreviewMode("structured");
    setPreviewEditMode(true);
    setPreviewLocale(locale);

    window.requestAnimationFrame(() => {
      previewLocaleCardRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, []);

  const handleFixLocaleIssuesWithAI = useCallback(
    async (locale: string) => {
      if (!selectedListing) return;

      const targetEntry = getPreviewEntry(selectedListing, locale);
      if (!targetEntry) return;

      const baseContent = ListingService.sanitizeListingContentForPlatform(
        targetEntry.content as IOSListingSchema | AndroidListingSchema,
        selectedListing.platform,
        { enforceMaxLengths: false },
      ) as IOSListingSchema | AndroidListingSchema;

      const issues = getContentGuidelineIssues(
        selectedListing.platform,
        baseContent,
      );
      if (issues.length === 0) {
        focusLocaleIssueEditor(locale);
        setSuccess(
          `${getLocaleDisplayLabel(locale)} does not have any guideline issues.`,
        );
        return;
      }

      const fieldConfigs = getEditableFieldConfigs(selectedListing.platform);
      let nextContent = baseContent;

      try {
        setFixingLocaleIssueKey(locale);
        setError(null);
        setSuccess(null);

        for (const issue of issues) {
          const fieldConfig = fieldConfigs.find(
            (field) => field.key === issue.key,
          );
          if (!fieldConfig) {
            continue;
          }

          const optimized = await generateOptimizedListingFieldValue({
            listing: selectedListing,
            locale,
            field: fieldConfig,
            content: nextContent,
            issue,
          });

          nextContent = buildUpdatedEditableContent(
            nextContent,
            fieldConfig.key,
            optimized.nextValue,
            selectedListing.platform,
          );
        }

        const updatedListing = await ListingService.updateListingEntryContent({
          listingId: selectedListing.listingId,
          locale,
          platform: selectedListing.platform,
          content: nextContent,
        });

        setListings((prev) =>
          prev.map((item) =>
            item.listingId === updatedListing.listingId ? updatedListing : item,
          ),
        );
        setSelectedListing(updatedListing);

        if (previewLocale === locale) {
          const refreshedEntry = getPreviewEntry(updatedListing, locale);
          if (refreshedEntry) {
            setEditableEntryContent(
              ListingService.sanitizeListingContentForPlatform(
                refreshedEntry.content as
                  | IOSListingSchema
                  | AndroidListingSchema,
                updatedListing.platform,
                { enforceMaxLengths: false },
              ) as IOSListingSchema | AndroidListingSchema,
            );
          }
        }

        focusLocaleIssueEditor(locale);
        setSuccess(
          `Fixed ${issues.length} issue${
            issues.length === 1 ? "" : "s"
          } in ${getLocaleDisplayLabel(locale)} with AI. Review and publish when ready.`,
        );
      } catch (fixError) {
        console.error(fixError);
        setError(`Failed to fix issues for ${getLocaleDisplayLabel(locale)}.`);
      } finally {
        setFixingLocaleIssueKey(null);
      }
    },
    [
      focusLocaleIssueEditor,
      getLocaleDisplayLabel,
      previewLocale,
      selectedListing,
    ],
  );

  const handleBulkFixListingIssues = useCallback(
    async (
      localeSummaries: Array<{
        locale: string;
        issues: Array<{
          key: EditableListingFieldKey;
          label: string;
          count: number;
          limit: number;
          overflow: number;
        }>;
      }>,
    ) => {
      if (!selectedListing) return;

      const localesWithIssues = localeSummaries.filter(
        (item) => item.issues.length > 0,
      );
      if (localesWithIssues.length === 0) {
        setSuccess("All locales are already compliant.");
        return;
      }

      const fieldConfigs = getEditableFieldConfigs(selectedListing.platform);
      let updatedListingState = selectedListing;
      let fixedFieldCount = 0;
      const failedLocales: Array<{
        locale: string;
        failedIssues: string[];
      }> = [];
      let successfulLocaleCount = 0;

      try {
        setBulkFixingIssues(true);
        setBulkFixLocaleStatuses({});
        setBulkFixProgress({
          currentLocale: null,
          completed: 0,
          total: localesWithIssues.length,
        });
        setError(null);
        setSuccess(null);

        for (const [
          localeIndex,
          localeSummary,
        ] of localesWithIssues.entries()) {
          setBulkFixProgress({
            currentLocale: localeSummary.locale,
            completed: localeIndex,
            total: localesWithIssues.length,
          });
          const entry = getPreviewEntry(
            updatedListingState,
            localeSummary.locale,
          );
          if (!entry) {
            continue;
          }

          let nextContent = ListingService.sanitizeListingContentForPlatform(
            entry.content as IOSListingSchema | AndroidListingSchema,
            updatedListingState.platform,
            { enforceMaxLengths: false },
          ) as IOSListingSchema | AndroidListingSchema;
          const localeFailures: string[] = [];
          let localeChanged = false;

          for (const issue of localeSummary.issues) {
            const fieldConfig = fieldConfigs.find(
              (field) => field.key === issue.key,
            );
            if (!fieldConfig) {
              continue;
            }

            try {
              const optimized = await generateOptimizedListingFieldValue({
                listing: updatedListingState,
                locale: localeSummary.locale,
                field: fieldConfig,
                content: nextContent,
                issue,
              });

              nextContent = buildUpdatedEditableContent(
                nextContent,
                fieldConfig.key,
                optimized.nextValue,
                updatedListingState.platform,
              );
              fixedFieldCount += 1;
              localeChanged = true;
            } catch (issueError) {
              console.error(issueError);
              localeFailures.push(fieldConfig.label);
            }
          }

          if (localeChanged) {
            updatedListingState =
              await ListingService.updateListingEntryContent({
                listingId: updatedListingState.listingId,
                locale: localeSummary.locale,
                platform: updatedListingState.platform,
                content: nextContent,
              });
            successfulLocaleCount += 1;
            setBulkFixLocaleStatuses((prev) => ({
              ...prev,
              [localeSummary.locale]: "success",
            }));
          }

          if (localeFailures.length > 0) {
            failedLocales.push({
              locale: localeSummary.locale,
              failedIssues: localeFailures,
            });
            setBulkFixLocaleStatuses((prev) => ({
              ...prev,
              [localeSummary.locale]: "error",
            }));
          } else if (!localeChanged) {
            setBulkFixLocaleStatuses((prev) => ({
              ...prev,
              [localeSummary.locale]: "error",
            }));
          }
        }

        setListings((prev) =>
          prev.map((item) =>
            item.listingId === updatedListingState.listingId
              ? updatedListingState
              : item,
          ),
        );
        setSelectedListing(updatedListingState);

        const refreshedEntry = getPreviewEntry(
          updatedListingState,
          previewLocale,
        );
        if (refreshedEntry) {
          setEditableEntryContent(
            ListingService.sanitizeListingContentForPlatform(
              refreshedEntry.content as IOSListingSchema | AndroidListingSchema,
              updatedListingState.platform,
              { enforceMaxLengths: false },
            ) as IOSListingSchema | AndroidListingSchema,
          );
        }

        if (fixedFieldCount > 0) {
          setSuccess(
            `Bulk-fixed ${fixedFieldCount} issue${
              fixedFieldCount === 1 ? "" : "s"
            } across ${successfulLocaleCount} locale${
              successfulLocaleCount === 1 ? "" : "s"
            }. Review and save/publish when ready.`,
          );
        }

        if (failedLocales.length > 0) {
          setError(
            `Some locales still need attention: ${failedLocales
              .map(
                (item) =>
                  `${getLocaleDisplayLabel(item.locale)} (${item.failedIssues.join(", ")})`,
              )
              .join("; ")}.`,
          );
        } else if (fixedFieldCount === 0) {
          setError(
            "Bulk fix could not resolve any of the selected locale issues.",
          );
        }
      } catch (bulkFixError) {
        console.error(bulkFixError);
        setError("Failed to bulk-fix listing issues with AI.");
      } finally {
        setBulkFixProgress({
          currentLocale: null,
          completed: 0,
          total: 0,
        });
        setBulkFixingIssues(false);
      }
    },
    [previewLocale, selectedListing],
  );

  const getEditableFieldConfigs = (platform: "ios" | "android") =>
    platform === "ios" ? IOS_EDITABLE_FIELDS : ANDROID_EDITABLE_FIELDS;

  const getEditableFieldValue = (
    content: IOSListingSchema | AndroidListingSchema,
    fieldKey: EditableListingFieldKey,
  ) => {
    if (fieldKey === "keywords") {
      return "keywords" in content && Array.isArray(content.keywords)
        ? content.keywords.join(", ")
        : "";
    }
    return String((content as any)[fieldKey] || "");
  };

  const parseKeywordsValue = (value: string) =>
    value
      .split(/\n|,/g)
      .map((keyword) => keyword.trim())
      .filter(Boolean)
      .filter((keyword, index, array) => array.indexOf(keyword) === index);

  const getKeywordsCharacterCount = (value: string | string[]) => {
    const keywords = Array.isArray(value) ? value : parseKeywordsValue(value);
    return keywords.join(",").length;
  };

  const getContentGuidelineIssues = (
    platform: "ios" | "android",
    content: IOSListingSchema | AndroidListingSchema,
  ) =>
    getEditableFieldConfigs(platform).flatMap((field) => {
      const fieldValue = getEditableFieldValue(content, field.key);
      const currentCount =
        field.key === "keywords"
          ? getKeywordsCharacterCount(fieldValue)
          : fieldValue.length;
      const limit =
        field.maxLength ||
        (field.key === "keywords" ? APP_STORE_KEYWORDS_MAX_CHARS : undefined);

      if (!limit || currentCount <= limit) {
        return [];
      }

      return [
        {
          key: field.key,
          label: field.label,
          count: currentCount,
          limit,
          overflow: currentCount - limit,
        },
      ];
    });

  const createDraftPlatforms = getCreateTargetPlatforms();
  const activeBaseDraftContent =
    baseListingDrafts[baseReviewPlatform] || null;
  const activeBaseDraftIssues =
    activeBaseDraftContent &&
    createDraftPlatforms.includes(baseReviewPlatform)
      ? getContentGuidelineIssues(baseReviewPlatform, activeBaseDraftContent)
      : [];

  const buildGuidelineIssuePromptLines = (issue?: {
    label: string;
    count: number;
    limit: number;
    overflow: number;
  }) => {
    if (!issue) {
      return [];
    }

    return [
      "Issue context:",
      `- The current value violates the store guideline for ${issue.label}.`,
      `- Current length/count: ${issue.count}.`,
      `- Allowed maximum: ${issue.limit}.`,
      `- It exceeds the limit by ${issue.overflow}.`,
      "- This limit is a hard requirement. Any response above the limit is invalid.",
      "- Fix the issue while preserving the intent, clarity, and conversion strength of the copy.",
      "- Do not return a blunt truncation. Rewrite intelligently so the result still reads naturally.",
      "- Remove filler words, repetition, and low-value phrasing first.",
      "- If the current wording cannot fit, rewrite from scratch into a shorter high-conversion version.",
    ];
  };

  const buildUpdatedEditableContent = (
    baseContent: IOSListingSchema | AndroidListingSchema,
    fieldKey: EditableListingFieldKey,
    nextValue: string,
    platform: "ios" | "android",
  ) => {
    if (fieldKey === "keywords") {
      return ListingService.sanitizeListingContentForPlatform(
        {
          ...baseContent,
          keywords: parseKeywordsValue(nextValue),
        },
        platform,
        { enforceMaxLengths: false },
      ) as IOSListingSchema | AndroidListingSchema;
    }

    return ListingService.sanitizeListingContentForPlatform(
      {
        ...baseContent,
        [fieldKey]: nextValue,
      } as IOSListingSchema | AndroidListingSchema,
      platform,
      { enforceMaxLengths: false },
    ) as IOSListingSchema | AndroidListingSchema;
  };

  const handleBaseDraftFieldChange = (
    platform: "ios" | "android",
    fieldKey: EditableListingFieldKey,
    value: string,
  ) => {
    setBaseListingDrafts((prev) => {
      const currentDraft = prev[platform];
      if (!currentDraft) return prev;
      return {
        ...prev,
        [platform]: buildUpdatedEditableContent(
          currentDraft,
          fieldKey,
          value,
          platform,
        ),
      };
    });
  };

  const resetCreateDialog = () => {
    setLocaleSupportFilter("common");
    setSelectedApp("");
    setFormData({
      appName: "",
      category: APP_CATEGORIES[0],
      features: ["", "", ""],
      platform: "ios",
      locales: [],
      targetAudience: "",
      keywords: ["", "", ""],
      supportUrl: "",
      marketingUrl: "",
      usp: ["", ""],
      includeWhatsNew: true,
      isFirstVersionApp: false,
      whatsNewTemplate: "bug_fixes_improvements",
      whatsNewCustomText: "",
      listingContextTemplates: [],
      listingContextCustomText: "",
    });
    setCreateSections({
      appSource: true,
      outputTarget: true,
      appContext: false,
      locales: false,
    });
    setCreateListingModel("gemini-2.5-flash-lite");
    setCreateListingStage("form");
    setCreateListingStatusIndex(0);
    setCreatedListingsResult([]);
    setBaseListingDrafts({});
    setBaseReviewPlatform("ios");
  };

  const truncate = (value: string | undefined, length: number) => {
    const text = (value || "").trim();
    if (text.length <= length) return text;
    return `${text.slice(0, length - 1).trimEnd()}...`;
  };

  const publishStoreLabel =
    publishTargetListing?.platform === "ios"
      ? "App Store Connect"
      : "Google Play";
  const publishValidation =
    publishTargetListing?.platform === "ios"
      ? getAppStoreConnectPublishValidation(publishTargetListing)
      : getGooglePlayPublishValidation(publishTargetListing);
  const publishLocaleOptions =
    publishLocaleFilter === "all_generated"
      ? publishValidation.generatedLocales
      : publishValidation.publishableLocales;
  const publishTargetLocales =
    publishMode === "single_locale"
      ? publishLocale &&
        publishValidation.publishableLocales.includes(publishLocale)
        ? [publishLocale]
        : []
      : publishMode === "selected_locales"
        ? publishSelectedLocales.filter((locale) =>
            publishValidation.publishableLocales.includes(locale),
          )
        : publishValidation.publishableLocales;
  const validationResults = validationResult
    ? Array.from(validationResult.results as readonly any[])
    : [];
  const validatedRequestedLocales = [
    ...validationResults.map((result) => result.locale),
  ].sort();
  const selectedPublishTargetLocales = [...publishTargetLocales].sort();
  const validationMatchesCurrentSelection =
    Boolean(validationResult) &&
    JSON.stringify(validatedRequestedLocales) ===
      JSON.stringify(selectedPublishTargetLocales);
  const validationHasErrors = Boolean(
    validationResults.some(
      (result) =>
        result.status === "error" ||
        Boolean(
          result.validationIssues?.some(
            (issue: any) => issue.severity === "error",
          ),
        ),
    ),
  );
  const validationIssueLocales =
    validationResults.filter(
      (result) =>
        result.status === "error" ||
        Boolean(
          result.validationIssues?.some(
            (issue: any) => issue.severity === "error",
          ),
        ),
    ) || [];
  const publishRequiresValidation =
    publishValidation.blockers.length === 0 && publishTargetLocales.length > 0;
  const publishBypassesValidation =
    publishRequiresValidation && publishSkipValidationOverride;
  const canPublishListing =
    !validatingGooglePlay &&
    !publishingToGooglePlay &&
    publishValidation.blockers.length === 0 &&
    !(
      (publishMode === "single_locale" && !publishLocale) ||
      (publishMode === "selected_locales" && publishTargetLocales.length === 0)
    ) &&
    (publishBypassesValidation ||
      (validationMatchesCurrentSelection &&
        (!validationHasErrors || publishIgnoreValidationIssues))) &&
    publishConfirmationChecked;
  const publishTargetCount = publishTargetLocales.length;
  const publishScopeLabel =
    publishMode === "single_locale"
      ? "Single locale"
      : publishMode === "selected_locales"
        ? "Selected locales"
        : "All locales";
  const publishCompletedSuccessfully =
    publishResult?.commitStatus === "committed";
  const validationStatus = !publishRequiresValidation
    ? {
        label: "Blocked",
        color: "warning" as const,
        message: "Resolve the publish blockers before validation can run.",
      }
    : !validationResult
      ? {
          label: "Validation required",
          color: "info" as const,
          message: `Fetch current ${publishStoreLabel} data before publishing is available.`,
        }
      : publishBypassesValidation
        ? {
            label: "Override enabled",
            color: "warning" as const,
            message: `Validation has been explicitly bypassed. Publishing will proceed without fetching current ${publishStoreLabel} data.`,
          }
        : !validationMatchesCurrentSelection
          ? {
              label: "Refresh required",
              color: "warning" as const,
              message: "The publish target changed after validation was run.",
            }
          : validationHasErrors
            ? {
                label: "Fix recommended",
                color: "warning" as const,
                message:
                  "One or more target locales have validation issues. Fix them first or explicitly publish anyway.",
              }
            : {
                label: "Ready to confirm",
                color: "success" as const,
                message: "Validation is current and publish can be confirmed.",
              };
  const previewEntry = getPreviewEntry(selectedListing, previewLocale);
  const savedPreviewContent =
    selectedListing && previewEntry
      ? (ListingService.sanitizeListingContentForPlatform(
          previewEntry.content as IOSListingSchema | AndroidListingSchema,
          selectedListing.platform,
          { enforceMaxLengths: false },
        ) as IOSListingSchema | AndroidListingSchema)
      : null;
  const activePreviewContent = editableEntryContent || savedPreviewContent;
  const hasUnsavedPreviewChanges =
    Boolean(savedPreviewContent && activePreviewContent) &&
    JSON.stringify(savedPreviewContent) !==
      JSON.stringify(activePreviewContent);

  useEffect(() => {
    if (!selectedListing) {
      setPreviewLocale("");
      setEditableEntryContent(null);
      setPreviewEditMode(false);
      return;
    }
    const locales = getListingLocales(selectedListing);
    if (!previewLocale || !locales.includes(previewLocale)) {
      setPreviewLocale(locales[0] || "");
    }
  }, [selectedListing]);

  useEffect(() => {
    if (validationResult) {
      setPublishSections((prev) => ({
        ...prev,
        preview: true,
        confirm: true,
      }));
    }
  }, [validationResult]);

  useEffect(() => {
    if (publishResult) {
      setPublishSections((prev) => ({
        ...prev,
        result: true,
      }));
    }
  }, [publishResult]);

  useEffect(() => {
    if (
      createListingStage !== "generating" &&
      createListingStage !== "generating_base"
    ) {
      setCreateListingStatusIndex(0);
      return;
    }

    const activeStatusMessages =
      createListingStage === "generating_base"
        ? BASE_DRAFT_STATUS_MESSAGES
        : CREATE_LISTING_STATUS_MESSAGES;
    const interval = window.setInterval(() => {
      setCreateListingStatusIndex((currentIndex) =>
        currentIndex < activeStatusMessages.length - 1
          ? currentIndex + 1
          : currentIndex,
      );
    }, 1600);

    return () => {
      window.clearInterval(interval);
    };
  }, [createListingStage]);

  useEffect(() => {
    if (!selectedListing) {
      setEditableEntryContent(null);
      return;
    }

    const entry = getPreviewEntry(selectedListing, previewLocale);
    if (!entry) {
      setEditableEntryContent(null);
      return;
    }

    setEditableEntryContent(
      ListingService.sanitizeListingContentForPlatform(
        entry.content as IOSListingSchema | AndroidListingSchema,
        selectedListing.platform,
        { enforceMaxLengths: false },
      ) as IOSListingSchema | AndroidListingSchema,
    );
  }, [previewLocale, selectedListing]);

  useEffect(() => {
    let active = true;
    const loadListingIcons = async () => {
      if (!user?.email || !selectedListing?.appId) {
        if (active) {
          setListingIcons([]);
          setSelectedListingIconUrl("");
        }
        return;
      }
      try {
        const icons = await IconService.getIconsByAppId(
          selectedListing.appId,
          user.email,
        );
        if (!active) return;
        const sorted = [...icons].sort(
          (a, b) =>
            b.metadata.generatedAt.getTime() - a.metadata.generatedAt.getTime(),
        );
        setListingIcons(sorted);
        setSelectedListingIconUrl((previous) =>
          previous && sorted.some((icon) => icon.baseImageUrl === previous)
            ? previous
            : sorted[0]?.baseImageUrl || "",
        );
      } catch (iconError) {
        console.error(iconError);
        if (active) {
          setListingIcons([]);
          setSelectedListingIconUrl("");
        }
      }
    };
    loadListingIcons();
    return () => {
      active = false;
    };
  }, [selectedListing?.appId, user?.email]);

  useEffect(() => {
    if (!listingFilterAppId) {
      setSelectedListing(null);
      return;
    }

    if (selectedListing && selectedListing.appId !== listingFilterAppId) {
      setSelectedListing(null);
    }
  }, [listingFilterAppId, selectedListing]);

  useEffect(() => {
    let active = true;
    const loadStoreFrameAssets = async () => {
      if (!user?.email || !selectedListing?.appId) {
        if (active) {
          setStoreFrameAssets([]);
          setStoreFrameLatestVersion(null);
        }
        return;
      }
      try {
        const assets = await ScreenshotService.getStoredScreenshotsByAppId(
          selectedListing.appId,
          user.email,
        );
        if (!active) return;

        const latestVersion =
          assets.length > 0
            ? Math.max(...assets.map((item) => Number(item.versionNumber || 0)))
            : null;
        setStoreFrameLatestVersion(latestVersion);
        setStoreFrameAssets(
          latestVersion === null
            ? []
            : assets.filter(
                (item) => Number(item.versionNumber || 0) === latestVersion,
              ),
        );
      } catch (assetError) {
        console.error(assetError);
        if (active) {
          setStoreFrameAssets([]);
          setStoreFrameLatestVersion(null);
        }
      }
    };
    loadStoreFrameAssets();
    return () => {
      active = false;
    };
  }, [selectedListing?.appId, user?.email]);

  const renderFramePreview = (
    listing: AppStoreListingType,
    entry: ListingLocaleEntry,
    storeIconUrl?: string,
    screenshots?: StoredStoreScreenshotAsset[],
  ) => {
    const content = getDisplayListingContent(
      entry.content as IOSListingSchema | AndroidListingSchema,
    );
    const guidelineIssues = getContentGuidelineIssues(
      listing.platform,
      content,
    );
    const titleGuidelineIssue = guidelineIssues.find(
      (issue) => issue.key === "title",
    );
    const titleText = String(content.title || "").trim();
    const isIOS = listing.platform === "ios";
    const iosContent = content as IOSListingSchema;
    const androidContent = content as AndroidListingSchema;
    const displayAppName = sanitizeDisplayText(listing.appName);
    const frameWidth = isIOS ? 430 : 390;
    const frameRadius = isIOS ? 56 : 36;
    const screenRadius = isIOS ? 48 : 28;

    const frameScreenshots = (screenshots || []).filter(
      (item) => item.platform === listing.platform,
    );
    const localeMatchedScreenshots = frameScreenshots.filter(
      (item) => item.locale?.toLowerCase() === entry.locale?.toLowerCase(),
    );
    const selectedScreenshots =
      localeMatchedScreenshots.length > 0
        ? localeMatchedScreenshots
        : frameScreenshots;
    const previewTiles = selectedScreenshots.slice(0, 2);

    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box
          sx={{
            width: frameWidth,
            maxWidth: "100%",
            bgcolor: "#0d0d0f",
            p: isIOS ? "10px" : "8px",
            borderRadius: `${frameRadius}px`,
            boxShadow: "0 24px 50px rgba(0,0,0,0.25)",
            border: "1px solid #1f1f24",
          }}
        >
          <Box
            sx={{
              height: isIOS ? 30 : 16,
              width: isIOS ? 138 : 10,
              borderRadius: isIOS ? 99 : 10,
              bgcolor: "#191b1f",
              border: isIOS ? "none" : "2px solid #2b2f36",
              mx: "auto",
              mb: 1,
            }}
          />
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: `${screenRadius}px`,
              p: 2,
              minHeight: isIOS ? 780 : 700,
            }}
          >
            <Typography variant="caption" color="textSecondary">
              {isIOS
                ? "App Store (iPhone 15 Pro Max)"
                : "Google Play (Android)"}{" "}
              - {entry.locale.toUpperCase()}
            </Typography>

            <Box
              sx={{ display: "flex", mt: 2, gap: 1.5, alignItems: "center" }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: "action.hover",
                  border: "1px solid #d0d7de",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {storeIconUrl ? (
                  <img
                    src={storeIconUrl}
                    alt={`${listing.appName} icon`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : null}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.1,
                    color: titleGuidelineIssue ? "error.main" : "text.primary",
                    bgcolor: titleGuidelineIssue
                      ? "rgba(211, 47, 47, 0.08)"
                      : "transparent",
                    border: titleGuidelineIssue ? "1px solid" : "none",
                    borderColor: titleGuidelineIssue
                      ? "error.light"
                      : "transparent",
                    borderRadius: 1,
                    px: titleGuidelineIssue ? 0.75 : 0,
                    py: titleGuidelineIssue ? 0.35 : 0,
                    wordBreak: "break-word",
                  }}
                >
                  {titleText || displayAppName}
                </Typography>
                {titleGuidelineIssue && (
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Title exceeds limit by {titleGuidelineIssue.overflow}{" "}
                    characters.
                  </Typography>
                )}
                <Typography variant="caption" color="textSecondary">
                  {truncate(displayAppName, 28)}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                sx={{ borderRadius: 99, textTransform: "none" }}
              >
                {isIOS ? "GET" : "Install"}
              </Button>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 1.5,
                mb: 1,
              }}
            >
              <Box>
                <Typography variant="caption" color="textSecondary">
                  4.8
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                >
                  Ratings
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  12+
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                >
                  Age
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  {isIOS ? "Lifestyle" : "Productivity"}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  color="textSecondary"
                >
                  Category
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              {previewTiles.length > 0 ? (
                previewTiles.map((asset) => (
                  <Box
                    key={asset.assetId}
                    sx={{
                      flex: 1,
                      height: 120,
                      borderRadius: 2,
                      bgcolor: "action.hover",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={asset.downloadUrl}
                      alt={`${listing.appName} screenshot`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                ))
              ) : (
                <>
                  <Box
                    sx={{
                      flex: 1,
                      height: 120,
                      borderRadius: 2,
                      bgcolor: "action.hover",
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      height: 120,
                      borderRadius: 2,
                      bgcolor: "action.hover",
                    }}
                  />
                </>
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {isIOS ? "What is it about?" : "About this app"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {isIOS
                  ? truncate(
                      `${iosContent.subtitle}. ${iosContent.promotionalText}`,
                      220,
                    )
                  : truncate(
                      `${androidContent.shortDescription}. ${androidContent.fullDescription}`,
                      260,
                    )}
              </Typography>
              <Typography
                variant="caption"
                color="primary"
                sx={{ fontWeight: 600 }}
              >
                {isIOS ? "Read more" : "See more"}
              </Typography>
            </Box>

            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                border: "1px solid #e8eaed",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {isIOS ? "Preview Text" : "Developer notes"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {isIOS
                  ? truncate(
                      iosContent.promotionalText || iosContent.description,
                      110,
                    )
                  : truncate(
                      androidContent.shortDescription ||
                        androidContent.fullDescription,
                      110,
                    )}
              </Typography>
            </Box>
            {isIOS && String(iosContent.whatsNew || "").trim() ? (
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid #e8eaed",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  What's New
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {truncate(iosContent.whatsNew || "", 180)}
                </Typography>
              </Box>
            ) : null}

            {"keywords" in content &&
            sanitizeDisplayKeywords(content.keywords).length > 0 ? (
              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {sanitizeDisplayKeywords(content.keywords)
                  .slice(0, 8)
                  .map((keyword) => (
                    <Chip
                      key={keyword}
                      size="small"
                      label={truncate(keyword, 18)}
                    />
                  ))}
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderCreateBaseFieldCard = (
    field: EditableListingFieldConfig,
    content: IOSListingSchema | AndroidListingSchema,
    platform: "ios" | "android",
  ) => {
    const fieldValue = getEditableFieldValue(content, field.key);
    const charCount =
      field.key === "keywords"
        ? getKeywordsCharacterCount(fieldValue)
        : fieldValue.length;
    const limitValue =
      field.maxLength ||
      (field.key === "keywords" ? APP_STORE_KEYWORDS_MAX_CHARS : undefined);
    const isOverLimit = Boolean(limitValue && charCount > limitValue);
    const limitLabel = field.maxLength
      ? `${charCount}/${field.maxLength} characters`
      : `${charCount}/${APP_STORE_KEYWORDS_MAX_CHARS} characters`;

    return (
      <Box
        key={`base-${platform}-${field.key}`}
        sx={{
          bgcolor: isOverLimit ? "rgba(211, 47, 47, 0.06)" : "action.hover",
          p: 2,
          borderRadius: 1.5,
          mb: 2,
          border: "1px solid",
          borderColor: isOverLimit ? "error.light" : "transparent",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {field.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {field.helperText}
            </Typography>
          </Box>
          {limitValue ? (
            <Chip
              size="small"
              color={isOverLimit ? "error" : "default"}
              label={limitLabel}
            />
          ) : null}
        </Box>
        {isOverLimit ? (
          <Alert severity="error" sx={{ mb: 1.25 }}>
            {field.key === "keywords"
              ? "Keywords exceed the 100-character App Store limit."
              : `${field.label} exceeds the allowed limit.`}
          </Alert>
        ) : null}
        <TextField
          fullWidth
          multiline={field.multiline || field.key === "keywords"}
          minRows={field.minRows || (field.key === "keywords" ? 3 : 1)}
          value={fieldValue}
          onChange={(event) =>
            handleBaseDraftFieldChange(platform, field.key, event.target.value)
          }
          placeholder={`Edit ${field.label.toLowerCase()}`}
          helperText={
            field.key === "keywords"
              ? "Use commas or line breaks between items."
              : ""
          }
          error={isOverLimit}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: "background.paper",
            },
          }}
        />
      </Box>
    );
  };

  const renderEditableFieldCard = (
    field: EditableListingFieldConfig,
    content: IOSListingSchema | AndroidListingSchema,
    editable = true,
  ) => {
    const fieldValue = getEditableFieldValue(content, field.key);
    const fieldTranslationKey = previewLocale
      ? `${previewLocale}:${field.key}`
      : field.key;
    const translationRecord = englishFieldTranslations[fieldTranslationKey];
    const activeTranslation =
      translationRecord && translationRecord.sourceValue === fieldValue
        ? translationRecord
        : null;
    const isTranslating = translatingFieldKey === fieldTranslationKey;
    const canTranslateToEnglish =
      !/^en(?:-|$)/i.test(previewLocale || "") && fieldValue.trim().length > 0;
    const charCount =
      field.key === "keywords"
        ? getKeywordsCharacterCount(fieldValue)
        : fieldValue.length;
    const limitValue =
      field.maxLength ||
      (field.key === "keywords" ? APP_STORE_KEYWORDS_MAX_CHARS : undefined);
    const isOverLimit = Boolean(limitValue && charCount > limitValue);
    const limitLabel = field.maxLength
      ? `${charCount}/${field.maxLength} characters`
      : `${charCount}/${APP_STORE_KEYWORDS_MAX_CHARS} characters`;
    const isCopied = copiedField === field.label;

    return (
      <Box
        key={field.key}
        sx={{
          bgcolor: isOverLimit ? "rgba(211, 47, 47, 0.06)" : "action.hover",
          p: 2,
          borderRadius: 1.5,
          mb: 2,
          border: "1px solid",
          borderColor: isOverLimit ? "error.light" : "transparent",
          boxShadow: isOverLimit ? "0 0 0 1px rgba(211, 47, 47, 0.08)" : "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            mb: 1,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              {field.label}
            </Typography>
            <Typography
              variant="caption"
              color={isOverLimit ? "error.main" : "text.secondary"}
            >
              {field.helperText}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Chip
              size="small"
              label={limitLabel}
              color={isOverLimit ? "error" : "default"}
              variant="outlined"
            />
            {isOverLimit && (
              <Chip
                size="small"
                color="error"
                label={
                  field.key === "keywords"
                    ? `Reduce by ${charCount - APP_STORE_KEYWORDS_MAX_CHARS} chars`
                    : `Reduce by ${charCount - (field.maxLength || 0)} chars`
                }
              />
            )}
            {editable && (
              <Button
                size="small"
                onClick={() => handleOptimizeListingField(field)}
                disabled={
                  optimizingFieldKey === field.key ||
                  savingEntryContent ||
                  bulkFixingIssues ||
                  Boolean(fixingLocaleIssueKey)
                }
              >
                {optimizingFieldKey === field.key ? (
                  <CircularProgress size={18} />
                ) : (
                  "Optimize with AI"
                )}
              </Button>
            )}
            {canTranslateToEnglish && (
              <Button
                size="small"
                variant="text"
                startIcon={
                  isTranslating ? (
                    <CircularProgress size={16} />
                  ) : (
                    <TranslateIcon fontSize="small" />
                  )
                }
                onClick={() => handleTranslateFieldToEnglish(field)}
                disabled={isTranslating || savingEntryContent}
              >
                {isTranslating ? "Translating..." : "Translate to English"}
              </Button>
            )}
            <Button
              size="small"
              color={isCopied ? "success" : "primary"}
              startIcon={
                isCopied ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <CopyIcon fontSize="small" />
                )
              }
              onClick={() =>
                handleCopyText(
                  field.key === "keywords"
                    ? formatKeywordsForCopy(parseKeywordsValue(fieldValue))
                    : fieldValue,
                  field.label,
                )
              }
            >
              {isCopied ? "Copied" : "Copy"}
            </Button>
          </Box>
        </Box>
        {isOverLimit && (
          <Alert severity="error" sx={{ mb: 1.25 }}>
            {field.key === "keywords"
              ? "This field exceeds the guideline limit. App Store keywords should stay within 100 total characters."
              : `${field.label} exceeds the guideline limit of ${field.maxLength} characters and should be shortened before publishing.`}
          </Alert>
        )}
        <TextField
          fullWidth
          multiline={field.multiline || field.key === "keywords"}
          minRows={field.minRows || (field.key === "keywords" ? 3 : 1)}
          value={fieldValue}
          onChange={
            editable
              ? (event) =>
                  handleEditableFieldChange(field.key, event.target.value)
              : undefined
          }
          placeholder={`Edit ${field.label.toLowerCase()}`}
          helperText={
            editable
              ? field.key === "keywords"
                ? "Use commas or line breaks between items."
                : ""
              : "Read-only preview. Switch to Edit mode to make changes."
          }
          error={isOverLimit}
          InputProps={{ readOnly: !editable }}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: "background.paper",
            },
          }}
        />
        {activeTranslation && (
          <Paper
            variant="outlined"
            sx={{
              mt: 1.25,
              p: 1.25,
              borderRadius: 1.5,
              borderColor: "success.light",
              bgcolor: "rgba(46, 125, 50, 0.05)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 1,
                flexDirection: { xs: "column", sm: "row" },
                mb: 0.5,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "success.dark" }}
              >
                English translation
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Reviewer helper • {activeTranslation.model}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}
            >
              {activeTranslation.translatedText}
            </Typography>
          </Paper>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold" }}>
        App Store Listing Creator
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Tabs
        value={tabValue}
        onChange={(e, newValue) => setTabValue(newValue)}
        sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 3 }}
      >
        <Tab
          label="Generate Listing"
          id="lst-tab-0"
          aria-controls="lst-tabpanel-0"
        />
        <Tab
          label="My Listings"
          id="lst-tab-1"
          aria-controls="lst-tabpanel-1"
        />
        <Tab
          label="Listing Preview"
          id="lst-tab-2"
          aria-controls="lst-tabpanel-2"
        />
        <Tab
          label="What's New Updater"
          id="lst-tab-3"
          aria-controls="lst-tabpanel-3"
        />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.8 }}>
            Create Store Listing
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use a registered app for auto-prefill, choose platform/locales, then
            generate listing content with schema-aligned output.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              label="1. Select App / Manual Entry"
              size="small"
              variant="outlined"
            />
            <Chip
              label="2. Set Platform & Locales"
              size="small"
              variant="outlined"
            />
            <Chip
              label="3. Generate & Preview"
              size="small"
              variant="outlined"
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
            onClick={() => {
              resetCreateDialog();
              setNewListingDialog(true);
            }}
          >
            New Listing
          </Button>
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Filter by Registered App</InputLabel>
            <Select
              value={listingFilterAppId}
              label="Filter by Registered App"
              onChange={(e) => setListingFilterAppId(e.target.value)}
            >
              <MenuItem value="">
                <em>Select App</em>
              </MenuItem>
              {apps.map((app) => (
                <MenuItem key={app.appId} value={app.appId}>
                  {app.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
        {loading ? (
          <CircularProgress />
        ) : !listingFilterAppId ? (
          <Alert severity="info">
            Select a registered app to load its listings.
          </Alert>
        ) : listings.length === 0 ? (
          <Alert severity="info">No listings yet.</Alert>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {filteredGroupedListings.map((group) => (
              <Paper key={group.key} sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                  {group.appName}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {group.dateGroups.map((dateGroup) => {
                    const dateGroupKey = getListingsDateGroupKey(
                      group.key,
                      dateGroup.key,
                    );
                    const isExpanded =
                      expandedListingsDateGroups[dateGroupKey] ??
                      isLatestDateGroup(group, dateGroup.key);

                    return (
                      <Accordion
                        key={`${group.key}-${dateGroup.key}`}
                        expanded={isExpanded}
                        onChange={(_event, expanded) =>
                          setExpandedListingsDateGroups((prev) => ({
                            ...prev,
                            [dateGroupKey]: expanded,
                          }))
                        }
                        disableGutters
                        sx={{
                          border: "1px solid rgba(148,163,184,0.2)",
                          borderRadius: "18px !important",
                          overflow: "hidden",
                          boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
                          "&:before": { display: "none" },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            background:
                              "linear-gradient(135deg, rgba(248,250,252,0.95) 0%, rgba(241,245,249,0.88) 100%)",
                            borderBottom: isExpanded
                              ? "1px solid rgba(148,163,184,0.14)"
                              : "none",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                              width: "100%",
                              pr: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Box>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ fontWeight: 800 }}
                              >
                                {dateGroup.label}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {dateGroup.items.length} listing job
                                {dateGroup.items.length === 1 ? "" : "s"}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.75,
                                flexWrap: "wrap",
                              }}
                            >
                              {dateGroup.items.some(
                                (item) => item.platform === "ios",
                              ) && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label="iOS"
                                />
                              )}
                              {dateGroup.items.some(
                                (item) => item.platform === "android",
                              ) && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label="Android"
                                />
                              )}
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.25 }}>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fit, minmax(260px, 1fr))",
                              gap: 2,
                            }}
                          >
                            {dateGroup.items.map((listing) => (
                              <Box key={listing.listingId}>
                                <Card
                                  variant="outlined"
                                  sx={{
                                    height: "100%",
                                    borderRadius: 2,
                                    borderColor: "divider",
                                    boxShadow: "0 6px 18px rgba(16,24,40,0.04)",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      boxShadow:
                                        "0 10px 24px rgba(16,24,40,0.10)",
                                      borderColor: "divider",
                                      transform: "translateY(-1px)",
                                    },
                                  }}
                                >
                                  <CardContent sx={{ pb: 1.2 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        mb: 1,
                                        gap: 1,
                                      }}
                                    >
                                      <Chip
                                        size="small"
                                        label={getPlatformLabel(
                                          listing.platform,
                                        )}
                                        icon={
                                          getPlatformIcon(listing.platform) ||
                                          undefined
                                        }
                                        sx={{
                                          fontWeight: 700,
                                          bgcolor: "action.hover",
                                          color: "#1548a6",
                                        }}
                                      />
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {getListingEntries(listing).length}{" "}
                                        entries
                                      </Typography>
                                    </Box>

                                    <Typography
                                      variant="subtitle2"
                                      sx={{ fontWeight: 700, mb: 0.8 }}
                                    >
                                      {listing.appName}
                                    </Typography>

                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.8,
                                        mb: 1.1,
                                      }}
                                    >
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={getListingVersionLabel(listing)}
                                      />
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`Generated ${formatListingDateLabel(
                                          getListingCreatedAt(listing),
                                        )}`}
                                      />
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`Category: ${listing.category}`}
                                      />
                                      {isCurrentPlayVersion(listing) && (
                                        <Chip
                                          size="small"
                                          color="success"
                                          label="Current version on Play store"
                                        />
                                      )}
                                    </Box>

                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block", mb: 0.4 }}
                                    >
                                      Locales
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 0.6,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      {getListingLocales(listing)
                                        .slice(0, 6)
                                        .map((locale: string) => (
                                          <Chip
                                            key={`${listing.listingId}-${locale}`}
                                            size="small"
                                            label={getLocaleDisplayLabel(
                                              locale,
                                            )}
                                            variant="outlined"
                                          />
                                        ))}
                                      {getListingLocales(listing).length >
                                        6 && (
                                        <Chip
                                          size="small"
                                          label={`+${
                                            getListingLocales(listing).length -
                                            6
                                          }`}
                                        />
                                      )}
                                    </Box>
                                  </CardContent>
                                  <CardActions
                                    sx={{
                                      pt: 1,
                                      borderTop: "1px solid",
                                      borderColor: "divider",
                                      justifyContent: "space-between",
                                      flexWrap: "wrap",
                                      gap: 1,
                                    }}
                                  >
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        setSelectedListing(listing);
                                        setTabValue(2);
                                      }}
                                    >
                                      Open version
                                    </Button>
                                    <Button
                                      size="small"
                                      startIcon={
                                        <CloudUploadIcon fontSize="small" />
                                      }
                                      onClick={() =>
                                        openPublishDialogForListing(listing)
                                      }
                                    >
                                      Publish
                                    </Button>
                                    <Button
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        handleDeleteListing(listing.listingId)
                                      }
                                    >
                                      Delete
                                    </Button>
                                  </CardActions>
                                </Card>
                              </Box>
                            ))}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Filter by Registered App</InputLabel>
            <Select
              value={listingFilterAppId}
              label="Filter by Registered App"
              onChange={(e) => setListingFilterAppId(e.target.value)}
            >
              <MenuItem value="">
                <em>Select App</em>
              </MenuItem>
              {apps.map((app) => (
                <MenuItem key={app.appId} value={app.appId}>
                  {app.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
        {listings.length === 0 ? (
          <Alert severity="info">No listings yet.</Alert>
        ) : !listingFilterAppId ? (
          <Alert severity="info">
            Select a registered app to load preview listings.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={5} lg={4.5}>
              <Paper sx={{ p: 2, height: "100%" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Select Listing
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  Browse listing jobs by generation date, then open a specific
                  iOS or Google Play version for preview, editing, or publish.
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {filteredGroupedListings.flatMap((group) =>
                    group.dateGroups.map((dateGroup) => {
                      const dateGroupKey = getPreviewDateGroupKey(
                        group.key,
                        dateGroup.key,
                      );
                      const isExpanded =
                        expandedPreviewDateGroups[dateGroupKey] ??
                        isLatestDateGroup(group, dateGroup.key);

                      return (
                        <Accordion
                          key={dateGroupKey}
                          expanded={isExpanded}
                          onChange={(_event, expanded) =>
                            setExpandedPreviewDateGroups((prev) => ({
                              ...prev,
                              [dateGroupKey]: expanded,
                            }))
                          }
                          disableGutters
                          sx={{
                            border: "1px solid rgba(148,163,184,0.24)",
                            borderRadius: "18px !important",
                            overflow: "hidden",
                            boxShadow: "0 8px 24px rgba(15,23,42,0.05)",
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              background:
                                "linear-gradient(135deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.92) 100%)",
                              borderBottom: isExpanded
                                ? "1px solid rgba(148,163,184,0.18)"
                                : "none",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                width: "100%",
                                pr: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 800 }}
                                >
                                  {dateGroup.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {dateGroup.items.length} listing job
                                  {dateGroup.items.length === 1 ? "" : "s"}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.75,
                                  flexWrap: "wrap",
                                }}
                              >
                                {dateGroup.items.some(
                                  (item) => item.platform === "ios",
                                ) && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label="iOS"
                                  />
                                )}
                                {dateGroup.items.some(
                                  (item) => item.platform === "android",
                                ) && (
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label="Android"
                                  />
                                )}
                              </Box>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 1.25 }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              {dateGroup.items.map((listing) => (
                                <Card
                                  key={listing.listingId}
                                  variant={
                                    selectedListing?.listingId ===
                                    listing.listingId
                                      ? "elevation"
                                      : "outlined"
                                  }
                                  sx={{
                                    borderRadius: 3,
                                    borderColor:
                                      selectedListing?.listingId ===
                                      listing.listingId
                                        ? "#7da4f8"
                                        : "rgba(148,163,184,0.28)",
                                    boxShadow:
                                      selectedListing?.listingId ===
                                      listing.listingId
                                        ? "0 14px 30px rgba(42,98,218,0.18)"
                                        : "0 8px 24px rgba(15,23,42,0.06)",
                                    overflow: "hidden",
                                    transition:
                                      "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
                                    "&:hover": {
                                      transform: "translateY(-1px)",
                                      boxShadow:
                                        selectedListing?.listingId ===
                                        listing.listingId
                                          ? "0 16px 32px rgba(42,98,218,0.22)"
                                          : "0 12px 28px rgba(15,23,42,0.10)",
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      px: 1.5,
                                      py: 1,
                                      borderBottom:
                                        "1px solid rgba(148,163,184,0.18)",
                                      background:
                                        selectedListing?.listingId ===
                                        listing.listingId
                                          ? "linear-gradient(135deg, rgba(66,133,244,0.14) 0%, rgba(52,211,153,0.08) 100%)"
                                          : "linear-gradient(135deg, rgba(248,250,252,0.95) 0%, rgba(241,245,249,0.85) 100%)",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                      }}
                                    >
                                      <Chip
                                        size="small"
                                        label={getPlatformLabel(
                                          listing.platform,
                                        )}
                                        icon={
                                          getPlatformIcon(listing.platform) ||
                                          undefined
                                        }
                                        sx={{
                                          fontWeight: 700,
                                          bgcolor:
                                            listing.platform === "android"
                                              ? "rgba(61,220,132,0.12)"
                                              : "rgba(17,24,39,0.06)",
                                          color:
                                            listing.platform === "android"
                                              ? "#1f7a4f"
                                              : "#374151",
                                        }}
                                      />
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`${getListingEntries(listing).length} entries`}
                                        sx={{
                                          bgcolor: "rgba(255,255,255,0.7)",
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                  <CardContent
                                    sx={{ pb: 1.25, px: 1.5, pt: 1.35 }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 800,
                                        mb: 0.9,
                                        fontSize: "1rem",
                                        lineHeight: 1.35,
                                        minHeight: 44,
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                      }}
                                    >
                                      {listing.appName}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        mb: 1,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {getListingLocales(listing).length}{" "}
                                        locale
                                        {getListingLocales(listing).length === 1
                                          ? ""
                                          : "s"}{" "}
                                        ready
                                      </Typography>
                                      {selectedListing?.listingId ===
                                        listing.listingId && (
                                        <Chip
                                          size="small"
                                          color="primary"
                                          label="Selected"
                                          sx={{ fontWeight: 700 }}
                                        />
                                      )}
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 0.6,
                                        flexWrap: "wrap",
                                        mb: 1,
                                      }}
                                    >
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={getListingVersionLabel(listing)}
                                      />
                                      {isCurrentPlayVersion(listing) && (
                                        <Chip
                                          size="small"
                                          color="success"
                                          label="Current version on Play store"
                                        />
                                      )}
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.6,
                                        minHeight: 64,
                                        alignContent: "flex-start",
                                      }}
                                    >
                                      {getListingLocales(listing)
                                        .slice(0, 4)
                                        .map((locale: string) => (
                                          <Chip
                                            key={`${listing.listingId}_${locale}`}
                                            label={getLocaleDisplayLabel(
                                              locale,
                                            )}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                              maxWidth: "100%",
                                              "& .MuiChip-label": {
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                              },
                                            }}
                                          />
                                        ))}
                                      {getListingLocales(listing).length >
                                        4 && (
                                        <Chip
                                          size="small"
                                          label={`+${getListingLocales(listing).length - 4}`}
                                          sx={{ bgcolor: "action.hover" }}
                                        />
                                      )}
                                    </Box>
                                  </CardContent>
                                  <CardActions
                                    sx={{
                                      pt: 0,
                                      px: 1.5,
                                      pb: 1.5,
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "stretch",
                                      gap: 1,
                                    }}
                                  >
                                    <Button
                                      size="small"
                                      variant={
                                        selectedListing?.listingId ===
                                        listing.listingId
                                          ? "contained"
                                          : "outlined"
                                      }
                                      onClick={() =>
                                        setSelectedListing(listing)
                                      }
                                      fullWidth
                                    >
                                      Open Preview
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="text"
                                      startIcon={
                                        <CloudUploadIcon fontSize="small" />
                                      }
                                      onClick={() =>
                                        openPublishDialogForListing(listing)
                                      }
                                      fullWidth
                                    >
                                      Publish
                                    </Button>
                                    <Button
                                      size="small"
                                      color="error"
                                      variant="text"
                                      onClick={() =>
                                        handleDeleteListing(listing.listingId)
                                      }
                                      fullWidth
                                    >
                                      Delete Job
                                    </Button>
                                  </CardActions>
                                </Card>
                              ))}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    }),
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7} lg={7.5}>
              {!selectedListing ? (
                <Alert severity="info">Select a listing to preview</Alert>
              ) : (
                <Paper sx={{ p: 2.5 }}>
                  {(() => {
                    const selectedApp = getAppForListing(selectedListing);
                    const lastPublishedDate = formatLastPublishedDate(
                      selectedApp?.googlePlayLastPublishedAt,
                    );
                    const selectedListingPublishedDate =
                      formatLastPublishedDate(
                        selectedListing.googlePlayPublishedAt,
                      );

                    return (
                      <Box
                        sx={{
                          p: { xs: 1.75, md: 2.25 },
                          borderRadius: 2,
                          mb: 2,
                          color: "text.primary",
                          background:
                            "linear-gradient(135deg, rgba(229,238,255,0.9) 0%, rgba(242,247,255,0.9) 55%, rgba(232,244,255,0.9) 100%)",
                          border: "1px solid #dbe7ff",
                          boxShadow: "0 14px 28px rgba(37, 99, 235, 0.08)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.8,
                                mb: 0.75,
                              }}
                            >
                              {getPlatformIcon(selectedListing.platform)}
                              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                {selectedListing.appName} •{" "}
                                {getPlatformLabel(selectedListing.platform)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.75,
                                flexWrap: "wrap",
                              }}
                            >
                              <Chip
                                size="small"
                                variant="outlined"
                                label={getListingVersionLabel(selectedListing)}
                              />
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Generated ${formatListingDateLabel(
                                  getListingCreatedAt(selectedListing),
                                )}`}
                              />
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`${getListingLocales(selectedListing).length} locales`}
                              />
                              {isCurrentPlayVersion(selectedListing) && (
                                <Chip
                                  size="small"
                                  color="success"
                                  label="Current version on Play store"
                                />
                              )}
                              {selectedListing.platform === "android" &&
                                selectedListingPublishedDate && (
                                  <Chip
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    label={`This version published: ${selectedListingPublishedDate}`}
                                  />
                                )}
                              {selectedListing.platform === "android" &&
                                lastPublishedDate && (
                                  <Chip
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    label={`Last published: ${lastPublishedDate}`}
                                  />
                                )}
                              <Chip
                                size="small"
                                variant="outlined"
                                label={
                                  previewMode === "frame"
                                    ? "Store frame preview"
                                    : "Structured preview"
                                }
                              />
                              {previewMode === "structured" && (
                                <Chip
                                  size="small"
                                  color={
                                    previewEditMode ? "warning" : "default"
                                  }
                                  variant="outlined"
                                  label={
                                    previewEditMode ? "Edit mode" : "View mode"
                                  }
                                />
                              )}
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              flexWrap: "wrap",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<CloudUploadIcon />}
                              onClick={() =>
                                openPublishDialogForListing(selectedListing)
                              }
                            >
                              {selectedListing.platform === "ios"
                                ? "Publish to App Store Connect"
                                : "Publish to Google Play"}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={() => exportVariants("text")}
                            >
                              Text
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              onClick={() => exportVariants("json")}
                            >
                              JSON
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })()}

                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid item xs={12} lg={5.2}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          height: "100%",
                          bgcolor: "rgba(248, 250, 252, 0.92)",
                          borderColor: "#dbe7ff",
                        }}
                      >
                        <Typography
                          variant="overline"
                          color="text.secondary"
                          sx={{ letterSpacing: 0.6 }}
                        >
                          Preview Setup
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                            mt: 0.75,
                          }}
                        >
                          <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={previewMode}
                            onChange={(
                              _,
                              value: "structured" | "frame" | null,
                            ) => {
                              if (value) {
                                setPreviewMode(value);
                                if (value === "frame") {
                                  setPreviewEditMode(false);
                                }
                              }
                            }}
                          >
                            <ToggleButton value="structured">
                              Structured
                            </ToggleButton>
                            <ToggleButton value="frame">
                              Store Frame
                            </ToggleButton>
                          </ToggleButtonGroup>
                          {previewMode === "structured" && (
                            <ToggleButtonGroup
                              size="small"
                              exclusive
                              value={previewEditMode ? "edit" : "view"}
                              onChange={(_, value: "view" | "edit" | null) => {
                                if (value) {
                                  setPreviewEditMode(value === "edit");
                                }
                              }}
                            >
                              <ToggleButton value="view">
                                View Mode
                              </ToggleButton>
                              <ToggleButton value="edit">
                                Edit Mode
                              </ToggleButton>
                            </ToggleButtonGroup>
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {previewMode === "frame"
                            ? "Inspect the store-facing composition and overflow behavior."
                            : previewEditMode
                              ? "Edit the active locale directly and resolve guideline issues."
                              : "Review the active locale in read-only mode before making changes."}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} lg={6.8}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: "background.paper",
                          borderColor: "#e2e8f0",
                        }}
                      >
                        <Typography
                          variant="overline"
                          color="text.secondary"
                          sx={{ letterSpacing: 0.6 }}
                        >
                          Active Context
                        </Typography>
                        <Grid container spacing={1.25} sx={{ mt: 0.1 }}>
                          <Grid item xs={12} sm={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Locale</InputLabel>
                              <Select
                                value={previewLocale}
                                label="Locale"
                                onChange={(e) =>
                                  setPreviewLocale(e.target.value)
                                }
                              >
                                {getListingLocales(selectedListing).map(
                                  (locale: string) => (
                                    <MenuItem
                                      key={`${selectedListing.listingId}_${locale}`}
                                      value={locale}
                                    >
                                      {getLocaleDisplayLabel(locale)}
                                    </MenuItem>
                                  ),
                                )}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Store Icon</InputLabel>
                              <Select
                                value={selectedListingIconUrl}
                                label="Store Icon"
                                onChange={(e) =>
                                  setSelectedListingIconUrl(e.target.value)
                                }
                              >
                                <MenuItem value="">No icon</MenuItem>
                                {listingIcons.map((icon) => (
                                  <MenuItem
                                    key={icon.iconId}
                                    value={icon.baseImageUrl}
                                  >
                                    {`${icon.metadata.generatedAt.toLocaleDateString()} • ${
                                      icon.metadata.aiModel
                                    }`}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>AI Model</InputLabel>
                              <Select
                                value={listingAiModel}
                                label="AI Model"
                                onChange={(e) =>
                                  setListingAiModel(e.target.value)
                                }
                              >
                                {AI_LISTING_MODELS.map((model) => (
                                  <MenuItem
                                    key={model.value}
                                    value={model.value}
                                  >
                                    {model.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.75,
                            flexWrap: "wrap",
                            mt: 1.25,
                          }}
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Active locale: ${getLocaleDisplayLabel(previewLocale)}`}
                          />
                          {storeFrameLatestVersion !== null && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Screenshots: Latest v${storeFrameLatestVersion}`}
                            />
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>

                  {(() => {
                    const localeSummaries = getListingEntries(
                      selectedListing,
                    ).map((item) => {
                      const resolvedContent =
                        item.locale === previewLocale && activePreviewContent
                          ? activePreviewContent
                          : (ListingService.sanitizeListingContentForPlatform(
                              item.content as
                                | IOSListingSchema
                                | AndroidListingSchema,
                              selectedListing.platform,
                              { enforceMaxLengths: false },
                            ) as IOSListingSchema | AndroidListingSchema);
                      const issues = getContentGuidelineIssues(
                        selectedListing.platform,
                        resolvedContent,
                      );

                      return {
                        locale: item.locale,
                        issues,
                      };
                    });
                    const localesWithIssues = localeSummaries.filter(
                      (item) => item.issues.length > 0,
                    );

                    return (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          mb: 2,
                          borderRadius: 2,
                          borderColor:
                            localesWithIssues.length > 0
                              ? "warning.light"
                              : "success.light",
                          bgcolor:
                            localesWithIssues.length > 0
                              ? "rgba(237, 108, 2, 0.05)"
                              : "rgba(46, 125, 50, 0.06)",
                          boxShadow:
                            localesWithIssues.length > 0
                              ? "0 10px 22px rgba(237, 108, 2, 0.08)"
                              : "0 10px 22px rgba(46, 125, 50, 0.08)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: { xs: "flex-start", sm: "center" },
                            gap: 1,
                            flexDirection: { xs: "column", sm: "row" },
                            mb: localesWithIssues.length > 0 ? 1.25 : 0,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 800, mb: 0.25 }}
                            >
                              Listing Overview
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Review locale-level guideline compliance before
                              editing or publishing.
                            </Typography>
                          </Box>
                          {localesWithIssues.length === 0 ? (
                            <Chip
                              color="success"
                              icon={<CheckCircleIcon />}
                              label="All locales are valid"
                              variant="outlined"
                            />
                          ) : (
                            <Box
                              sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                            >
                              <Chip
                                color="warning"
                                label={`${localesWithIssues.length} locale${
                                  localesWithIssues.length === 1 ? "" : "s"
                                } with issues`}
                                variant="outlined"
                              />
                              <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                startIcon={
                                  bulkFixingIssues ? (
                                    <CircularProgress
                                      size={16}
                                      color="inherit"
                                    />
                                  ) : (
                                    <EditIcon />
                                  )
                                }
                                onClick={() =>
                                  handleBulkFixListingIssues(localeSummaries)
                                }
                                disabled={
                                  bulkFixingIssues ||
                                  fixingLocaleIssueKey !== null
                                }
                                sx={{ textTransform: "none" }}
                              >
                                {bulkFixingIssues
                                  ? "Fixing all issues..."
                                  : "Bulk fix issues"}
                              </Button>
                            </Box>
                          )}
                        </Box>

                        {bulkFixingIssues && bulkFixProgress.total > 0 && (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.25,
                              mb: 1.25,
                              borderRadius: 1.75,
                              borderColor: "warning.light",
                              bgcolor: "rgba(255,255,255,0.72)",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: { xs: "flex-start", sm: "center" },
                                gap: 1,
                                flexDirection: { xs: "column", sm: "row" },
                                mb: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <CircularProgress size={18} color="warning" />
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  Fixing locale issues with AI
                                </Typography>
                              </Box>
                              <Chip
                                size="small"
                                color="warning"
                                variant="outlined"
                                label={`${Math.min(
                                  bulkFixProgress.completed + 1,
                                  bulkFixProgress.total,
                                )}/${bulkFixProgress.total}`}
                              />
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              Currently fixing:{" "}
                              {bulkFixProgress.currentLocale
                                ? getLocaleDisplayLabel(
                                    bulkFixProgress.currentLocale,
                                  )
                                : "Preparing..."}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={
                                bulkFixProgress.total > 0
                                  ? (bulkFixProgress.completed /
                                      bulkFixProgress.total) *
                                    100
                                  : 0
                              }
                              sx={{
                                height: 8,
                                borderRadius: 999,
                                bgcolor: "rgba(237, 108, 2, 0.12)",
                              }}
                            />
                          </Paper>
                        )}

                        {localesWithIssues.length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            {localesWithIssues.map((item) =>
                              (() => {
                                const localeStatus =
                                  bulkFixLocaleStatuses[item.locale];
                                const isActive =
                                  bulkFixingIssues &&
                                  bulkFixProgress.currentLocale === item.locale;

                                return (
                                  <Paper
                                    key={`overview-${item.locale}`}
                                    variant="outlined"
                                    sx={{
                                      p: 1.2,
                                      borderRadius: 1.75,
                                      bgcolor:
                                        localeStatus === "success"
                                          ? "rgba(46,125,50,0.08)"
                                          : localeStatus === "error"
                                            ? "rgba(255,235,238,0.72)"
                                            : isActive
                                              ? "rgba(255,243,224,0.95)"
                                              : "rgba(255,255,255,0.52)",
                                      borderColor:
                                        localeStatus === "success"
                                          ? "success.main"
                                          : localeStatus === "error"
                                            ? "error.light"
                                            : isActive
                                              ? "warning.main"
                                              : "rgba(237, 108, 2, 0.18)",
                                      boxShadow:
                                        localeStatus === "success"
                                          ? "0 0 0 1px rgba(46,125,50,0.18), 0 10px 24px rgba(46,125,50,0.14)"
                                          : isActive
                                            ? "0 0 0 1px rgba(237,108,2,0.14), 0 10px 24px rgba(237,108,2,0.14)"
                                            : "none",
                                      transition:
                                        "background-color 220ms ease, box-shadow 220ms ease, border-color 220ms ease",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: {
                                          xs: "flex-start",
                                          sm: "center",
                                        },
                                        gap: 1,
                                        flexDirection: {
                                          xs: "column",
                                          sm: "row",
                                        },
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: {
                                            xs: "flex-start",
                                            sm: "center",
                                          },
                                          gap: 1,
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: 800 }}
                                        >
                                          {getLocaleDisplayLabel(item.locale)}
                                        </Typography>
                                        <Chip
                                          size="small"
                                          color={
                                            localeStatus === "success"
                                              ? "success"
                                              : localeStatus === "error"
                                                ? "error"
                                                : "warning"
                                          }
                                          variant="outlined"
                                          label={`${item.issues.length} issue${
                                            item.issues.length === 1 ? "" : "s"
                                          }`}
                                        />
                                        {isActive && (
                                          <Chip
                                            size="small"
                                            color="warning"
                                            icon={
                                              <CircularProgress
                                                size={12}
                                                color="inherit"
                                              />
                                            }
                                            label="Fixing now"
                                            sx={{
                                              "& .MuiChip-label": {
                                                fontWeight: 700,
                                              },
                                            }}
                                          />
                                        )}
                                        {localeStatus === "success" && (
                                          <Chip
                                            size="small"
                                            color="success"
                                            icon={
                                              <CheckCircleIcon fontSize="small" />
                                            }
                                            label="Fixed successfully"
                                            sx={{
                                              "& .MuiChip-label": {
                                                fontWeight: 700,
                                              },
                                            }}
                                          />
                                        )}
                                        {localeStatus === "error" &&
                                          !isActive && (
                                            <Chip
                                              size="small"
                                              color="error"
                                              variant="outlined"
                                              label="Needs review"
                                              sx={{
                                                "& .MuiChip-label": {
                                                  fontWeight: 700,
                                                },
                                              }}
                                            />
                                          )}
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="warning"
                                          startIcon={
                                            fixingLocaleIssueKey ===
                                              item.locale || isActive ? (
                                              <CircularProgress
                                                size={16}
                                                color="inherit"
                                              />
                                            ) : (
                                              <EditIcon />
                                            )
                                          }
                                          onClick={() =>
                                            handleFixLocaleIssuesWithAI(
                                              item.locale,
                                            )
                                          }
                                          sx={{
                                            minWidth: 0,
                                            borderRadius: 999,
                                            px: 1.2,
                                            py: 0.35,
                                            textTransform: "none",
                                          }}
                                          disabled={
                                            bulkFixingIssues ||
                                            Boolean(
                                              fixingLocaleIssueKey &&
                                              fixingLocaleIssueKey !==
                                                item.locale,
                                            )
                                          }
                                        >
                                          {fixingLocaleIssueKey === item.locale
                                            ? "Fixing..."
                                            : "Fix issue with AI"}
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="warning"
                                          onClick={() =>
                                            focusLocaleIssueEditor(item.locale)
                                          }
                                          sx={{
                                            minWidth: 0,
                                            borderRadius: 999,
                                            px: 1.2,
                                            py: 0.35,
                                            textTransform: "none",
                                          }}
                                        >
                                          Open locale
                                        </Button>
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {item.issues
                                          .map(
                                            (issue) =>
                                              `${issue.label} +${issue.overflow} (${issue.count}/${issue.limit})`,
                                          )
                                          .join(" · ")}
                                      </Typography>
                                    </Box>
                                  </Paper>
                                );
                              })(),
                            )}
                          </Box>
                        )}
                      </Paper>
                    );
                  })()}

                  {(() => {
                    const entry =
                      getListingEntries(selectedListing).find(
                        (item) => item.locale === previewLocale,
                      ) || getListingEntries(selectedListing)[0];
                    if (!entry) {
                      return (
                        <Alert severity="info">
                          No locale content found for this listing.
                        </Alert>
                      );
                    }

                    const displayContent = getDisplayListingContent(
                      (activePreviewContent || entry.content) as
                        | IOSListingSchema
                        | AndroidListingSchema,
                    );
                    const displayAppName = sanitizeDisplayText(
                      selectedListing.appName,
                    );

                    return previewMode === "frame" ? (
                      renderFramePreview(
                        selectedListing,
                        {
                          ...entry,
                          content: (activePreviewContent || entry.content) as
                            | IOSListingSchema
                            | AndroidListingSchema,
                        },
                        selectedListingIconUrl,
                        storeFrameAssets,
                      )
                    ) : (
                      <Card
                        ref={previewLocaleCardRef}
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <Chip label={getLocaleDisplayLabel(entry.locale)} />
                            <Typography variant="caption" color="textSecondary">
                              {selectedListing.platform === "ios"
                                ? "Apple App Store schema"
                                : "Google Play schema"}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              mb: 2,
                              p: 1.4,
                              borderRadius: 1.5,
                              border: "1px solid #e5eaf2",
                              bgcolor: "background.paper",
                              display: "flex",
                              alignItems: "center",
                              gap: 1.2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 54,
                                height: 54,
                                borderRadius: 1.5,
                                overflow: "hidden",
                                border: "1px solid #d8dee9",
                                bgcolor: "action.hover",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {selectedListingIconUrl ? (
                                <img
                                  src={selectedListingIconUrl}
                                  alt={`${selectedListing.appName} icon`}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : null}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700, lineHeight: 1.2 }}
                              >
                                {displayAppName}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {getPlatformLabel(selectedListing.platform)}
                              </Typography>
                            </Box>
                          </Box>

                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              mb: 2,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: { xs: "flex-start", sm: "center" },
                              gap: 1.5,
                              flexDirection: { xs: "column", sm: "row" },
                            }}
                          >
                            <Box>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 700 }}
                              >
                                {previewEditMode
                                  ? "Editable locale content"
                                  : "Locale preview"}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {previewEditMode
                                  ? "Update this locale directly, then save to persist the listing. Use AI per field with store-aware limits and app context."
                                  : "Review this locale in read-only mode first. Switch to Edit mode when you want to change the listing text."}
                              </Typography>
                            </Box>
                            {previewEditMode && (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Button
                                  size="small"
                                  onClick={handleResetPreviewEdits}
                                  disabled={
                                    !hasUnsavedPreviewChanges ||
                                    savingEntryContent
                                  }
                                >
                                  Reset
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={handleSavePreviewEdits}
                                  disabled={
                                    !hasUnsavedPreviewChanges ||
                                    savingEntryContent
                                  }
                                >
                                  {savingEntryContent ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    "Save locale edits"
                                  )}
                                </Button>
                              </Box>
                            )}
                          </Paper>

                          {getEditableFieldConfigs(
                            selectedListing.platform,
                          ).map((field) =>
                            renderEditableFieldCard(
                              field,
                              displayContent,
                              previewEditMode,
                            ),
                          )}
                        </CardContent>
                      </Card>
                    );
                  })()}
                </Paper>
              )}
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Paper sx={{ p: 2.5, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <NewReleasesIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              What's New Updater
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fetch the current App Store Connect listing locales and push updated
            What's New release notes to all of them — without touching any other
            metadata.
          </Typography>

          {/* App selector */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Select iOS App</InputLabel>
            <Select
              value={whatsNewAppId}
              label="Select iOS App"
              onChange={(e) => {
                setWhatsNewAppId(e.target.value);
                setWhatsNewFetchResult(null);
                setWhatsNewTranslations({});
                setWhatsNewPushResult(null);
                setWhatsNewError(null);
                setWhatsNewSuccess(null);
              }}
            >
              <MenuItem value="">
                <em>— Select App —</em>
              </MenuItem>
              {apps
                .filter((app) => isIosCapableApp(app))
                .map((app) => (
                  <MenuItem key={app.appId} value={app.appId}>
                    {app.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={
              whatsNewFetching ? (
                <CircularProgress size={16} />
              ) : (
                <RefreshIcon />
              )
            }
            onClick={handleFetchWhatsNew}
            disabled={whatsNewFetching || !whatsNewAppId}
            sx={{ mb: 2 }}
          >
            {whatsNewFetching ? "Fetching…" : "Fetch Current Listing"}
          </Button>

          {whatsNewError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setWhatsNewError(null)}
            >
              {whatsNewError}
            </Alert>
          )}
          {whatsNewSuccess && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setWhatsNewSuccess(null)}
            >
              {whatsNewSuccess}
            </Alert>
          )}
        </Paper>

        {whatsNewFetchResult && whatsNewFetchResult.locales.length > 0 && (
          <>
            <Paper sx={{ p: 2.5, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                New What's New Content
              </Typography>
              {whatsNewFetchResult.appStoreVersionString && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  Target version:{" "}
                  <strong>{whatsNewFetchResult.appStoreVersionString}</strong>
                  {" · "}
                  {whatsNewFetchResult.locales.length} locale
                  {whatsNewFetchResult.locales.length !== 1 ? "s" : ""}
                </Typography>
              )}

              <TextField
                label="Base What's New text (English)"
                multiline
                minRows={4}
                fullWidth
                value={whatsNewBaseText}
                onChange={(e) => setWhatsNewBaseText(e.target.value)}
                placeholder="Enter your release notes in English…"
                helperText={`${whatsNewBaseText.length} / 4000 characters`}
                inputProps={{ maxLength: 4000 }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  startIcon={
                    whatsNewTranslating ? (
                      <CircularProgress size={16} sx={{ color: "inherit" }} />
                    ) : (
                      <AutoFixHighIcon />
                    )
                  }
                  onClick={handleTranslateWhatsNew}
                  disabled={whatsNewTranslating || !whatsNewBaseText.trim()}
                >
                  {whatsNewTranslating
                    ? "Translating…"
                    : `Translate to All ${whatsNewFetchResult.locales.length} Locales`}
                </Button>
                <Button
                  variant="text"
                  startIcon={<TranslateIcon />}
                  onClick={() => {
                    const filled: Record<string, string> = {};
                    for (const item of whatsNewFetchResult.locales) {
                      filled[item.locale] = whatsNewBaseText;
                    }
                    setWhatsNewTranslations(filled);
                  }}
                  disabled={!whatsNewBaseText.trim()}
                >
                  Apply Same Text to All
                </Button>
              </Box>
            </Paper>

            <Paper sx={{ p: 2.5, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Review Translations
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {whatsNewFetchResult.locales.map((item) => {
                  const localeOption = getStoreLocaleOption(item.locale);
                  const displayLabel = getStoreLocaleDisplayLabel(item.locale);
                  const currentValue =
                    whatsNewTranslations[item.locale] ?? item.whatsNew;
                  const charCount = currentValue.length;
                  return (
                    <Box key={item.locale}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        {localeOption?.flag && (
                          <Typography component="span">
                            {localeOption.flag}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {displayLabel}
                        </Typography>
                        <Chip
                          label={item.locale}
                          size="small"
                          variant="outlined"
                          sx={{ ml: "auto" }}
                        />
                        {item.whatsNew && (
                          <Tooltip title="Current live value">
                            <Chip
                              label="Live"
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </Box>
                      {item.whatsNew && currentValue !== item.whatsNew && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "block",
                            mb: 0.5,
                            fontStyle: "italic",
                          }}
                        >
                          Current:{" "}
                          {item.whatsNew.length > 80
                            ? item.whatsNew.slice(0, 80) + "…"
                            : item.whatsNew}
                        </Typography>
                      )}
                      <TextField
                        multiline
                        minRows={3}
                        fullWidth
                        size="small"
                        value={currentValue}
                        onChange={(e) =>
                          setWhatsNewTranslations((prev) => ({
                            ...prev,
                            [item.locale]: e.target.value,
                          }))
                        }
                        helperText={`${charCount} / 4000`}
                        error={charCount > 4000}
                        inputProps={{ maxLength: 4000 }}
                      />
                      {whatsNewPushResult && (
                        <>
                          {whatsNewPushResult.results.find(
                            (r) =>
                              r.locale === item.locale &&
                              r.status === "success",
                          ) && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                mt: 0.5,
                              }}
                            >
                              <CheckCircleIcon
                                sx={{ fontSize: 14, color: "success.main" }}
                              />
                              <Typography
                                variant="caption"
                                color="success.main"
                              >
                                Updated
                              </Typography>
                            </Box>
                          )}
                          {whatsNewPushResult.results.find(
                            (r) =>
                              r.locale === item.locale && r.status === "error",
                          ) && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ display: "block", mt: 0.5 }}
                            >
                              {
                                whatsNewPushResult.results.find(
                                  (r) => r.locale === item.locale,
                                )?.message
                              }
                            </Typography>
                          )}
                        </>
                      )}
                      <Divider sx={{ mt: 1.5 }} />
                    </Box>
                  );
                })}
              </Box>
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={
                  whatsNewPushing ? (
                    <CircularProgress size={18} sx={{ color: "inherit" }} />
                  ) : (
                    <CloudUploadIcon />
                  )
                }
                onClick={handlePushWhatsNew}
                disabled={
                  whatsNewPushing ||
                  Object.values(whatsNewTranslations).every((v) => !v.trim())
                }
              >
                {whatsNewPushing
                  ? "Pushing…"
                  : "Push What's New to App Store Connect"}
              </Button>
            </Box>
          </>
        )}

        {whatsNewFetchResult && whatsNewFetchResult.locales.length === 0 && (
          <Alert severity="info">
            No editable locales found on App Store Connect for this app.
          </Alert>
        )}
      </TabPanel>

      <Dialog
        open={publishDialogOpen}
        onClose={closePublishDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            background:
              "linear-gradient(180deg, rgba(232,245,233,0.92) 0%, rgba(255,255,255,1) 100%)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}
              >
                {publishTargetListing?.platform === "ios" ? (
                  <AppleIcon sx={{ color: "success.main" }} />
                ) : (
                  <AndroidIcon sx={{ color: "success.main" }} />
                )}
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {publishTargetListing?.platform === "ios"
                    ? "Publish to App Store Connect"
                    : "Publish to Google Play"}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Review the exact package, locale scope, and text diff before
                anything is committed to {publishStoreLabel}.
              </Typography>
            </Box>
            <Chip
              label={validationStatus.label}
              color={validationStatus.color}
              variant={
                validationStatus.color === "info" ? "outlined" : "filled"
              }
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#f8fafc" }}>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert severity="info">
              Clicking `Publish` on the listing card does not publish
              immediately. It opens this guided review flow. You must validate
              the target first, review the diff, and explicitly confirm before
              the final publish call is enabled.
            </Alert>

            <Accordion
              expanded={publishSections.overview}
              onChange={handlePublishSectionToggle("overview")}
              sx={{ borderRadius: 2.5, overflow: "hidden" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1.5,
                    width: "100%",
                    pr: 1,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Publish Overview
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      The guarded 3-step flow before any store metadata update
                      is committed.
                    </Typography>
                  </Box>
                  <Chip
                    label={`${publishTargetCount} target locale${
                      publishTargetCount === 1 ? "" : "s"
                    }`}
                    color="success"
                    variant="outlined"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
                  }}
                >
                  <Grid container spacing={1.5}>
                    {[
                      {
                        step: "1",
                        title: "Choose target",
                        description:
                          "Pick all locales or one locale for this listing.",
                        done: publishTargetCount > 0,
                        active: publishTargetCount > 0,
                      },
                      {
                        step: "2",
                        title: "Validate",
                        description: `Fetch live ${publishStoreLabel} metadata and compare the text.`,
                        done:
                          Boolean(validationResult) &&
                          validationMatchesCurrentSelection &&
                          !validationHasErrors,
                        active: Boolean(validationResult),
                      },
                      {
                        step: "3",
                        title: "Confirm",
                        description: `Explicitly approve the reviewed write to ${publishStoreLabel}.`,
                        done: canPublishListing,
                        active:
                          Boolean(validationResult) &&
                          validationMatchesCurrentSelection &&
                          !validationHasErrors,
                      },
                    ].map((step) => (
                      <Grid item xs={12} md={4} key={step.step}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            height: "100%",
                            borderRadius: 2,
                            borderColor: step.done
                              ? "success.light"
                              : step.active
                                ? "primary.light"
                                : "divider",
                            bgcolor: step.done
                              ? "rgba(76, 175, 80, 0.08)"
                              : step.active
                                ? "rgba(25, 118, 210, 0.08)"
                                : "background.paper",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: step.done
                                  ? "success.main"
                                  : step.active
                                    ? "primary.main"
                                    : "grey.300",
                                color:
                                  step.done || step.active
                                    ? "#fff"
                                    : "text.secondary",
                                fontWeight: 800,
                                fontSize: 13,
                              }}
                            >
                              {step.done ? (
                                <CheckCircleIcon sx={{ fontSize: 16 }} />
                              ) : (
                                step.step
                              )}
                            </Box>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700 }}
                            >
                              {step.title}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {step.description}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={publishSections.target}
              onChange={handlePublishSectionToggle("target")}
              sx={{ borderRadius: 2.5, overflow: "hidden" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1.5,
                    width: "100%",
                    pr: 1,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Publish Target
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Verify the app, package, and locale scope.
                    </Typography>
                  </Box>
                  <Chip
                    label={publishScopeLabel}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
                  }}
                >
                  <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        App
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {publishValidation.app?.name ||
                          publishTargetListing?.appName ||
                          "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        App Identifier
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, fontFamily: "monospace" }}
                      >
                        {publishValidation.packageName || "Not configured"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Listing
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {publishTargetListing?.appName || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Available locales
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {publishValidation.publishableLocales.length}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Generated locales
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {publishValidation.generatedLocales.length}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={1.5}>
                    <Grid
                      item
                      xs={12}
                      md={publishMode === "all_locales" ? 12 : 6}
                    >
                      <FormControl fullWidth>
                        <InputLabel>Publish Mode</InputLabel>
                        <Select
                          value={publishMode}
                          label="Publish Mode"
                          onChange={(e) => {
                            setPublishMode(
                              e.target.value as GooglePlayPublishMode,
                            );
                            setValidationResult(null);
                            setPublishResult(null);
                            setPublishConfirmationChecked(false);
                          }}
                        >
                          <MenuItem value="all_locales">
                            Publish all locales in this listing
                          </MenuItem>
                          <MenuItem value="single_locale">
                            Publish one locale only
                          </MenuItem>
                          <MenuItem value="selected_locales">
                            Publish selected locales
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {publishMode === "single_locale" && (
                      <Grid item xs={12} md={6}>
                        <ToggleButtonGroup
                          value={publishLocaleFilter}
                          exclusive
                          size="small"
                          onChange={(
                            _event,
                            value: "supported" | "all_generated" | null,
                          ) => {
                            if (!value) return;
                            setPublishLocaleFilter(value);
                          }}
                          sx={{ mb: 1, width: "100%" }}
                        >
                          <ToggleButton value="supported" sx={{ flex: 1 }}>
                            Supported only
                          </ToggleButton>
                          <ToggleButton value="all_generated" sx={{ flex: 1 }}>
                            All generated
                          </ToggleButton>
                        </ToggleButtonGroup>
                        <FormControl fullWidth>
                          <InputLabel>Locale</InputLabel>
                          <Select
                            value={publishLocale}
                            label="Locale"
                            onChange={(e) => {
                              setPublishLocale(e.target.value);
                              setValidationResult(null);
                              setPublishResult(null);
                              setPublishConfirmationChecked(false);
                            }}
                          >
                            {publishLocaleOptions.map((locale) => {
                              const isSupported =
                                publishValidation.publishableLocales.includes(
                                  locale,
                                );
                              return (
                                <MenuItem
                                  key={locale}
                                  value={locale}
                                  disabled={!isSupported}
                                >
                                  {getLocaleDisplayLabel(locale)}
                                  {!isSupported
                                    ? " (Unsupported for platform)"
                                    : ""}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    {publishMode === "selected_locales" && (
                      <Grid item xs={12} md={6}>
                        <ToggleButtonGroup
                          value={publishLocaleFilter}
                          exclusive
                          size="small"
                          onChange={(
                            _event,
                            value: "supported" | "all_generated" | null,
                          ) => {
                            if (!value) return;
                            setPublishLocaleFilter(value);
                          }}
                          sx={{ mb: 1, width: "100%" }}
                        >
                          <ToggleButton value="supported" sx={{ flex: 1 }}>
                            Supported only
                          </ToggleButton>
                          <ToggleButton value="all_generated" sx={{ flex: 1 }}>
                            All generated
                          </ToggleButton>
                        </ToggleButtonGroup>
                        <FormControl fullWidth>
                          <InputLabel>Locales</InputLabel>
                          <Select
                            multiple
                            value={publishSelectedLocales}
                            label="Locales"
                            onChange={(e) => {
                              const value = e.target.value;
                              setPublishSelectedLocales(
                                Array.isArray(value)
                                  ? value
                                  : String(value).split(","),
                              );
                              setValidationResult(null);
                              setPublishResult(null);
                              setPublishConfirmationChecked(false);
                            }}
                            renderValue={(selected) =>
                              Array.isArray(selected) && selected.length > 0
                                ? selected
                                    .map((locale) =>
                                      getLocaleDisplayLabel(locale),
                                    )
                                    .join(", ")
                                : "Choose locales"
                            }
                          >
                            {publishLocaleOptions.map((locale) => {
                              const isSupported =
                                publishValidation.publishableLocales.includes(
                                  locale,
                                );
                              return (
                                <MenuItem
                                  key={locale}
                                  value={locale}
                                  disabled={!isSupported}
                                >
                                  {getLocaleDisplayLabel(locale)}
                                  {!isSupported
                                    ? " (Unsupported for platform)"
                                    : ""}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>

                  {publishLocaleFilter === "all_generated" &&
                    publishValidation.skippedLocales.length > 0 && (
                      <Alert severity="info" sx={{ mt: 1.5 }}>
                        Unsupported locales are visible for review but cannot be
                        selected for publishing.
                      </Alert>
                    )}

                  <Box
                    sx={{
                      mt: 1.75,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "grey.50",
                      border: "1px dashed",
                      borderColor: "divider",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: { xs: "flex-start", sm: "center" },
                        gap: 1,
                        flexDirection: { xs: "column", sm: "row" },
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Target Locales Preview
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {publishTargetCount === 0
                          ? "Choose a locale target to continue"
                          : `${publishTargetCount} locale${
                              publishTargetCount === 1 ? "" : "s"
                            } queued for review`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {publishTargetLocales.map((locale) => (
                        <Chip
                          key={locale}
                          size="small"
                          label={getLocaleDisplayLabel(locale)}
                          variant="outlined"
                        />
                      ))}
                      {publishTargetLocales.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No target locales selected.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              </AccordionDetails>
            </Accordion>

            {publishValidation.blockers.length > 0 && (
              <Alert severity="warning">
                {publishValidation.blockers.join(" ")}
              </Alert>
            )}

            {publishValidation.warnings.length > 0 && (
              <Alert severity="info">
                {publishValidation.warnings.join(" ")}
              </Alert>
            )}

            <Accordion
              expanded={publishSections.validate}
              onChange={handlePublishSectionToggle("validate")}
              sx={{ borderRadius: 2.5, overflow: "hidden" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1.5,
                    width: "100%",
                    pr: 1,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      {publishTargetListing?.platform === "ios"
                        ? "Validate Current App Store Listing"
                        : "Validate Current Play Listing"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {publishTargetListing?.platform === "ios"
                        ? "Fetch live App Store Connect data and compare before publish."
                        : "Fetch live Play data and compare before publish."}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={validationStatus.label}
                    color={validationStatus.color}
                    variant="outlined"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1.5,
                      alignItems: { xs: "flex-start", sm: "center" },
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 800 }}
                        >
                          {publishTargetListing?.platform === "ios"
                            ? "Validate Current App Store Listing"
                            : "Validate Current Play Listing"}
                        </Typography>
                        <Chip
                          size="small"
                          label={validationStatus.label}
                          color={validationStatus.color}
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {validationStatus.message}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {publishTargetListing?.platform === "ios"
                          ? "Fetch the current App Store Connect metadata and compare it with this generated listing before publishing."
                          : "Fetch the current Google Play metadata from a draft edit and compare it with this generated listing before publishing."}
                      </Typography>
                    </Box>
                    <Button
                      onClick={handleValidateGooglePlayListing}
                      variant="contained"
                      color="inherit"
                      size="medium"
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { xs: "100%", sm: 0 },
                        alignSelf: { xs: "stretch", sm: "flex-start" },
                        px: 2,
                        py: 0.85,
                        fontSize: "0.875rem",
                        lineHeight: 1.2,
                        whiteSpace: "normal",
                        bgcolor: "#111827",
                        color: "#fff",
                        "&:hover": {
                          bgcolor: "#0f172a",
                        },
                      }}
                      disabled={
                        validatingGooglePlay ||
                        publishingToGooglePlay ||
                        publishValidation.blockers.length > 0 ||
                        (publishMode === "single_locale" && !publishLocale) ||
                        (publishMode === "selected_locales" &&
                          publishTargetLocales.length === 0)
                      }
                    >
                      {validatingGooglePlay ? (
                        <CircularProgress size={20} sx={{ color: "#fff" }} />
                      ) : publishTargetListing?.platform === "ios" ? (
                        "Fetch Current App Store Data"
                      ) : (
                        "Fetch Current Play Data"
                      )}
                    </Button>
                  </Box>
                </Paper>
              </AccordionDetails>
            </Accordion>

            {validationResult && (
              <Accordion
                expanded={publishSections.preview}
                onChange={handlePublishSectionToggle("preview")}
                sx={{ borderRadius: 2.5, overflow: "hidden" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      pr: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Validation Preview
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Review the per-locale live vs proposed text diff.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={validationPreviewLayout}
                        onChange={(
                          _event,
                          value: ValidationPreviewLayout | null,
                        ) => {
                          if (value) {
                            setValidationPreviewLayout(value);
                          }
                        }}
                      >
                        <ToggleButton value="stacked">Stacked</ToggleButton>
                        <ToggleButton value="side_by_side">
                          Side by side
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <Chip
                        label={`Edit ID: ${validationResult.editId || "N/A"}`}
                        variant="outlined"
                        sx={{ fontFamily: "monospace" }}
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      {validationResults.map((result) => {
                        const severity =
                          result.status === "error"
                            ? "error"
                            : result.status === "skipped"
                              ? "warning"
                              : result.status === "missing"
                                ? "warning"
                                : "success";
                        const isIosResult =
                          publishTargetListing?.platform === "ios";
                        const changeCount = result.changes
                          ? isIosResult
                            ? [
                                (
                                  result.changes as AppStoreConnectValidationResponse["results"][number]["changes"]
                                )?.title.changed,
                                (
                                  result.changes as AppStoreConnectValidationResponse["results"][number]["changes"]
                                )?.subtitle.changed,
                                (
                                  result.changes as AppStoreConnectValidationResponse["results"][number]["changes"]
                                )?.promotionalText.changed,
                                (
                                  result.changes as AppStoreConnectValidationResponse["results"][number]["changes"]
                                )?.whatsNew.changed,
                                (
                                  result.changes as AppStoreConnectValidationResponse["results"][number]["changes"]
                                )?.description.changed,
                                (
                                  result.changes as AppStoreConnectValidationResponse["results"][number]["changes"]
                                )?.keywords.changed,
                              ].filter(Boolean).length
                            : [
                                (
                                  result.changes as GooglePlayValidationResponse["results"][number]["changes"]
                                )?.title.changed,
                                (
                                  result.changes as GooglePlayValidationResponse["results"][number]["changes"]
                                )?.shortDescription.changed,
                                (
                                  result.changes as GooglePlayValidationResponse["results"][number]["changes"]
                                )?.fullDescription.changed,
                              ].filter(Boolean).length
                          : 0;
                        const hasGuidelineIssues = Boolean(
                          result.validationIssues?.some(
                            (issue: any) => issue.severity === "error",
                          ),
                        );
                        const iosChanges =
                          result.changes as AppStoreConnectValidationResponse["results"][number]["changes"];
                        const googleChanges =
                          result.changes as GooglePlayValidationResponse["results"][number]["changes"];
                        return (
                          <Paper
                            key={`validation-${result.locale}`}
                            variant="outlined"
                            sx={{
                              p: 1.75,
                              borderRadius: 2,
                              borderLeft: "4px solid",
                              borderLeftColor:
                                severity === "error"
                                  ? "error.main"
                                  : severity === "warning"
                                    ? "warning.main"
                                    : "success.main",
                              bgcolor: "background.paper",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: { xs: "flex-start", sm: "center" },
                                gap: 1.25,
                                flexDirection: { xs: "column", sm: "row" },
                                mb: result.changes ? 1.5 : 0,
                              }}
                            >
                              <Alert
                                severity={severity}
                                sx={{ flex: 1, mb: 0 }}
                              >
                                {getLocaleDisplayLabel(result.locale)}
                                {result.playLocale &&
                                result.playLocale !== result.locale
                                  ? ` -> ${result.playLocale}`
                                  : ""}
                                : {result.message}
                              </Alert>
                              {result.changes && (
                                <Chip
                                  size="small"
                                  color={
                                    hasGuidelineIssues || changeCount > 0
                                      ? "warning"
                                      : severity === "warning"
                                        ? "warning"
                                        : "success"
                                  }
                                  label={
                                    hasGuidelineIssues
                                      ? "Guideline issue"
                                      : changeCount > 0
                                        ? `${changeCount} field${
                                            changeCount === 1 ? "" : "s"
                                          } changing`
                                        : "No text changes"
                                  }
                                />
                              )}
                            </Box>
                            {result.validationIssues &&
                              result.validationIssues.length > 0 && (
                                <Alert severity="error" sx={{ mb: 1.5 }}>
                                  {result.validationIssues
                                    .map((issue: any) => issue.message)
                                    .join(" ")}
                                </Alert>
                              )}
                            {result.changes && (
                              <Grid container spacing={1.5}>
                                <Grid item xs={12}>
                                  {renderValidationFieldComparison({
                                    label: "Title",
                                    current: result.changes.title.current,
                                    proposed: result.changes.title.proposed,
                                    limit: GOOGLE_PLAY_FIELD_LIMITS.title,
                                  })}
                                </Grid>
                                {isIosResult ? (
                                  <>
                                    <Grid item xs={12}>
                                      {renderValidationFieldComparison({
                                        label: "Subtitle",
                                        current:
                                          iosChanges?.subtitle.current || "",
                                        proposed:
                                          iosChanges?.subtitle.proposed || "",
                                        limit: 30,
                                      })}
                                    </Grid>
                                    <Grid item xs={12}>
                                      {renderValidationFieldComparison({
                                        label: "Promotional Text",
                                        current:
                                          iosChanges?.promotionalText.current ||
                                          "",
                                        proposed:
                                          iosChanges?.promotionalText
                                            .proposed || "",
                                        limit: 170,
                                        multiline: true,
                                      })}
                                    </Grid>
                                    <Grid item xs={12}>
                                      {renderValidationFieldComparison({
                                        label: "Description",
                                        current:
                                          iosChanges?.description.current || "",
                                        proposed:
                                          iosChanges?.description.proposed ||
                                          "",
                                        limit: 4000,
                                        multiline: true,
                                      })}
                                    </Grid>
                                    <Grid item xs={12}>
                                      {renderValidationFieldComparison({
                                        label: "What's New",
                                        current:
                                          iosChanges?.whatsNew.current || "",
                                        proposed:
                                          iosChanges?.whatsNew.proposed || "",
                                        limit: 4000,
                                        multiline: true,
                                      })}
                                    </Grid>
                                    <Grid item xs={12}>
                                      {renderValidationFieldComparison({
                                        label: "Keywords",
                                        current:
                                          iosChanges?.keywords.current || "",
                                        proposed:
                                          iosChanges?.keywords.proposed || "",
                                        limit: APP_STORE_KEYWORDS_MAX_CHARS,
                                      })}
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: 0.75,
                                        }}
                                      >
                                        {[
                                          [
                                            "Title",
                                            result.changes.title.changed,
                                          ],
                                          [
                                            "Subtitle",
                                            iosChanges?.subtitle.changed,
                                          ],
                                          [
                                            "Promotional text",
                                            iosChanges?.promotionalText.changed,
                                          ],
                                          [
                                            "Description",
                                            iosChanges?.description.changed,
                                          ],
                                          [
                                            "What's New",
                                            iosChanges?.whatsNew.changed,
                                          ],
                                          [
                                            "Keywords",
                                            iosChanges?.keywords.changed,
                                          ],
                                        ].map(([label, changed]) => (
                                          <Chip
                                            key={`${result.locale}-${label}`}
                                            size="small"
                                            color={
                                              changed ? "warning" : "success"
                                            }
                                            label={
                                              changed
                                                ? `${label} will change`
                                                : `${label} unchanged`
                                            }
                                          />
                                        ))}
                                      </Box>
                                    </Grid>
                                  </>
                                ) : (
                                  <>
                                    <Grid item xs={12}>
                                      {renderValidationFieldComparison({
                                        label: "Short Description",
                                        current:
                                          googleChanges?.shortDescription
                                            .current || "",
                                        proposed:
                                          googleChanges?.shortDescription
                                            .proposed || "",
                                        limit:
                                          GOOGLE_PLAY_FIELD_LIMITS.shortDescription,
                                      })}
                                    </Grid>
                                    <Grid item xs={12}>
                                      {renderValidationFieldComparison({
                                        label: "Full Description",
                                        current:
                                          googleChanges?.fullDescription
                                            .current || "",
                                        proposed:
                                          googleChanges?.fullDescription
                                            .proposed || "",
                                        limit:
                                          GOOGLE_PLAY_FIELD_LIMITS.fullDescription,
                                        multiline: true,
                                      })}
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: 0.75,
                                        }}
                                      >
                                        <Chip
                                          size="small"
                                          color={
                                            result.changes.title.changed
                                              ? "warning"
                                              : "success"
                                          }
                                          label={
                                            result.changes.title.changed
                                              ? "Title will change"
                                              : "Title unchanged"
                                          }
                                        />
                                        <Chip
                                          size="small"
                                          color={
                                            googleChanges?.shortDescription
                                              .changed
                                              ? "warning"
                                              : "success"
                                          }
                                          label={
                                            googleChanges?.shortDescription
                                              .changed
                                              ? "Short description will change"
                                              : "Short description unchanged"
                                          }
                                        />
                                        <Chip
                                          size="small"
                                          color={
                                            googleChanges?.fullDescription
                                              .changed
                                              ? "warning"
                                              : "success"
                                          }
                                          label={
                                            googleChanges?.fullDescription
                                              .changed
                                              ? "Full description will change"
                                              : "Full description unchanged"
                                          }
                                        />
                                      </Box>
                                    </Grid>
                                  </>
                                )}
                              </Grid>
                            )}
                          </Paper>
                        );
                      })}
                    </Box>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}

            <Accordion
              expanded={publishSections.confirm}
              onChange={handlePublishSectionToggle("confirm")}
              sx={{ borderRadius: 2.5, overflow: "hidden" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1.5,
                    width: "100%",
                    pr: 1,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Publish Confirmation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Final approval before the publish action is enabled.
                    </Typography>
                  </Box>
                  <Chip
                    label={
                      publishConfirmationChecked &&
                      (!validationHasErrors || publishIgnoreValidationIssues)
                        ? "Confirmed"
                        : "Waiting for confirmation"
                    }
                    color={
                      publishConfirmationChecked &&
                      (!validationHasErrors || publishIgnoreValidationIssues)
                        ? "success"
                        : "default"
                    }
                    variant="outlined"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
                  }}
                >
                  {!publishRequiresValidation ? (
                    <Alert severity="warning">
                      Resolve the publish blockers above before validation and
                      confirmation are available.
                    </Alert>
                  ) : !validationResult ? (
                    <>
                      <Alert severity="info" sx={{ mb: 1.25 }}>
                        Run validation first. Publishing stays locked until
                        validation has been completed for the current target
                        locale set, unless you explicitly bypass validation
                        below.
                      </Alert>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: "rgba(211, 47, 47, 0.04)",
                          borderColor: publishSkipValidationOverride
                            ? "warning.main"
                            : "rgba(15,23,42,0.12)",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={publishSkipValidationOverride}
                              onChange={(e) => {
                                setPublishSkipValidationOverride(
                                  e.target.checked,
                                );
                                setPublishConfirmationChecked(false);
                              }}
                            />
                          }
                          label={`Skip current ${publishStoreLabel} data validation and unlock publish anyway.`}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Use this only when you intentionally want to publish
                          without fetching the current store listing first.
                        </Typography>
                      </Paper>
                      {publishSkipValidationOverride && (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            mt: 1.25,
                            bgcolor: "rgba(245, 158, 11, 0.08)",
                            borderColor: "warning.light",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={publishConfirmationChecked}
                                onChange={(e) =>
                                  setPublishConfirmationChecked(
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label={`I want to publish ${publishTargetLocales.length} target locale${
                              publishTargetLocales.length === 1 ? "" : "s"
                            } without running current ${publishStoreLabel} data validation.`}
                          />
                        </Paper>
                      )}
                    </>
                  ) : !validationMatchesCurrentSelection ? (
                    <Alert severity="warning">
                      Validation results are stale for the current publish
                      target. Re-run validation after changing the mode or
                      locale.
                    </Alert>
                  ) : validationHasErrors ? (
                    <>
                      <Alert severity="warning" sx={{ mb: 1.5 }}>
                        Validation found issues in{" "}
                        {validationIssueLocales.length} locale
                        {validationIssueLocales.length === 1 ? "" : "s"}. Fixing
                        them is recommended so you do not publish broken store
                        copy to {publishStoreLabel}.
                      </Alert>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          mb: 1.25,
                          borderColor: "warning.light",
                          bgcolor: "rgba(245, 158, 11, 0.08)",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, mb: 0.75 }}
                        >
                          Locales needing attention
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.75,
                          }}
                        >
                          {validationIssueLocales.map((result) => (
                            <Alert
                              key={`confirm-issue-${result.locale}`}
                              severity="warning"
                              sx={{ mb: 0 }}
                            >
                              {getLocaleDisplayLabel(result.locale)}
                              {result.playLocale &&
                              result.playLocale !== result.locale
                                ? ` -> ${result.playLocale}`
                                : ""}
                              :{" "}
                              {result.validationIssues?.length
                                ? result.validationIssues
                                    .map((issue: any) => issue.message)
                                    .join(" ")
                                : result.message}
                            </Alert>
                          ))}
                        </Box>
                      </Paper>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: "rgba(76, 175, 80, 0.06)",
                          borderColor: publishConfirmationChecked
                            ? "success.light"
                            : "rgba(15,23,42,0.12)",
                          mb: 1.25,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={publishConfirmationChecked}
                              onChange={(e) =>
                                setPublishConfirmationChecked(e.target.checked)
                              }
                            />
                          }
                          label={`I reviewed the validation results for ${publishTargetLocales.length} target locale${
                            publishTargetLocales.length === 1 ? "" : "s"
                          }.`}
                        />
                      </Paper>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: "rgba(211, 47, 47, 0.04)",
                          borderColor: publishIgnoreValidationIssues
                            ? "warning.main"
                            : "rgba(15,23,42,0.12)",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={publishIgnoreValidationIssues}
                              onChange={(e) =>
                                setPublishIgnoreValidationIssues(
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label="Publish anyway and ignore the current locale validation issues."
                        />
                        <Typography variant="caption" color="text.secondary">
                          Use this only if you intentionally want to push the
                          current text despite the warnings above.
                        </Typography>
                      </Paper>
                    </>
                  ) : (
                    <>
                      <Alert severity="success" sx={{ mb: 1.5 }}>
                        Validation completed for {publishTargetLocales.length}{" "}
                        target locale
                        {publishTargetLocales.length === 1 ? "" : "s"}.
                        Publishing will commit changes to{" "}
                        <Box component="span" sx={{ fontFamily: "monospace" }}>
                          {publishValidation.packageName}
                        </Box>
                        .
                      </Alert>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: "rgba(76, 175, 80, 0.08)",
                          borderColor: "success.light",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={publishConfirmationChecked}
                              onChange={(e) =>
                                setPublishConfirmationChecked(e.target.checked)
                              }
                            />
                          }
                          label={`I reviewed the validation results and want to publish ${publishTargetLocales.length} locale${
                            publishTargetLocales.length === 1 ? "" : "s"
                          } to ${publishStoreLabel}.`}
                        />
                      </Paper>
                    </>
                  )}
                </Paper>
              </AccordionDetails>
            </Accordion>

            {publishResult && (
              <Accordion
                expanded={publishSections.result}
                onChange={handlePublishSectionToggle("result")}
                sx={{ borderRadius: 2.5, overflow: "hidden" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      pr: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Publish Result
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Per-locale response from the latest publish attempt.
                      </Typography>
                    </Box>
                    <Chip
                      label={publishResult.commitStatus}
                      color={
                        publishResult.commitStatus === "committed"
                          ? "success"
                          : "warning"
                      }
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
                    }}
                  >
                    {publishCompletedSuccessfully && (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          mb: 2,
                          borderRadius: 2.5,
                          borderColor: "success.light",
                          bgcolor: "rgba(76, 175, 80, 0.08)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: { xs: "flex-start", sm: "center" },
                            flexDirection: { xs: "column", sm: "row" },
                            gap: 1.5,
                            mb: 1.25,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <CheckCircleIcon color="success" />
                            <Box>
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 800 }}
                              >
                                {`Published to ${publishStoreLabel}`}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                The selected listing was committed successfully
                                and is now the latest published store version.
                              </Typography>
                            </Box>
                          </Box>
                          <Chip color="success" label="Publish complete" />
                        </Box>
                        <Box
                          sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Package: ${publishResult.packageName}`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${publishResult.attemptedLocales.length} locale${
                              publishResult.attemptedLocales.length === 1
                                ? ""
                                : "s"
                            } attempted`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Scope: ${publishScopeLabel}`}
                          />
                          {publishResult.lastPublishedAt && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Published: ${formatLastPublishedDate(
                                publishResult.lastPublishedAt,
                              )}`}
                            />
                          )}
                        </Box>
                      </Paper>
                    )}
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Edit ID:{" "}
                      <Box component="span" sx={{ fontFamily: "monospace" }}>
                        {publishResult.editId || "N/A"}
                      </Box>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      Commit status: {publishResult.commitStatus}
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {publishResult.results.map((result) => (
                        <Alert
                          key={`${result.locale}-${result.status}`}
                          severity={
                            result.status === "success"
                              ? "success"
                              : result.status === "skipped"
                                ? "warning"
                                : "error"
                          }
                        >
                          {getLocaleDisplayLabel(result.locale)}
                          {result.playLocale &&
                          result.playLocale !== result.locale
                            ? ` -> ${result.playLocale}`
                            : ""}
                          : {result.message}
                        </Alert>
                      ))}
                    </Box>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {canPublishListing
              ? publishBypassesValidation
                ? "Validation is bypassed. Final publish is unlocked."
                : "Validation is current. Final publish is unlocked."
              : validationStatus.message}
          </Typography>
          <Button
            onClick={closePublishDialog}
            disabled={publishingToGooglePlay || validatingGooglePlay}
          >
            {publishCompletedSuccessfully ? "Back to Listing Preview" : "Close"}
          </Button>
          {!publishCompletedSuccessfully && (
            <Button
              onClick={handlePublishToGooglePlay}
              variant="contained"
              startIcon={<CloudUploadIcon />}
              disabled={!canPublishListing}
              sx={{ minWidth: 220 }}
            >
              {publishingToGooglePlay ? (
                <CircularProgress size={24} />
              ) : publishBypassesValidation ? (
                "Override and Publish"
              ) : (
                `Publish to ${publishStoreLabel}`
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={newListingDialog}
        onClose={() => {
          if (
            createListingStage === "generating" ||
            createListingStage === "generating_base"
          )
            return;
          setNewListingDialog(false);
          resetCreateDialog();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {createListingStage === "generating" ||
          createListingStage === "generating_base"
            ? "Generating App Store Listing"
            : createListingStage === "base_review"
              ? "Review Base English Listing"
            : createListingStage === "success"
              ? "Listing Generation Complete"
              : "Create App Store Listing"}
        </DialogTitle>
        <DialogContent>
          {createListingStage === "generating" ||
          createListingStage === "generating_base" ? (
            <Box sx={{ mt: 1.5, py: 1 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  borderColor: "#dbe7ff",
                  background:
                    "linear-gradient(135deg, rgba(239,246,255,0.95) 0%, rgba(248,250,252,0.96) 100%)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <CircularProgress size={24} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      {createListingStage === "generating_base"
                        ? "Generating base English draft"
                        : "Generating localized listings"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formData.locales.length} locale
                      {formData.locales.length === 1 ? "" : "s"} across{" "}
                      {formData.platform === "both"
                        ? "iOS and Android"
                        : getPlatformLabel(formData.platform)}
                      {formData.platform !== "android"
                        ? effectiveIncludeWhatsNew
                          ? " with iOS What's New"
                          : " without iOS What's New"
                        : ""}
                      {hasListingContextNote
                        ? " and context note"
                        : " without context note"}
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress
                  sx={{ borderRadius: 999, height: 8, mb: 1.5 }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
                  {(createListingStage === "generating_base"
                    ? BASE_DRAFT_STATUS_MESSAGES
                    : CREATE_LISTING_STATUS_MESSAGES)[createListingStatusIndex]}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {createListingStage === "generating_base"
                    ? "Preparing the English source listing that will be used to generate all selected locales."
                    : "The listing generator is building localized store text, applying platform rules, and saving each generated version for preview, editing, and publishing."}
                </Typography>
              </Paper>
            </Box>
          ) : createListingStage === "base_review" ? (
            <Box sx={{ mt: 1.5, py: 0.5 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Review and edit the base English listing below. The selected
                locales will be generated from this approved source.
              </Alert>
              {createDraftPlatforms.length > 1 ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    mb: 1.5,
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Base platform draft
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={baseReviewPlatform}
                    onChange={(_, value: "ios" | "android" | null) => {
                      if (value) {
                        setBaseReviewPlatform(value);
                      }
                    }}
                  >
                    {createDraftPlatforms.map((platform) => (
                      <ToggleButton key={platform} value={platform}>
                        {getPlatformLabel(platform)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Paper>
              ) : null}
              {!activeBaseDraftContent ? (
                <Alert severity="error">
                  Base English draft is not available. Go back and generate it
                  again.
                </Alert>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    borderColor: "divider",
                    bgcolor: "rgba(248,250,252,0.72)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      flexWrap: "wrap",
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {getPlatformLabel(baseReviewPlatform)} base copy
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Locales selected: ${formData.locales.length}`}
                    />
                  </Box>
                  {activeBaseDraftIssues.length > 0 ? (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      {activeBaseDraftIssues.length} field
                      {activeBaseDraftIssues.length === 1 ? "" : "s"} exceed
                      store limits. Adjust before generating locales.
                    </Alert>
                  ) : (
                    <Alert severity="success" sx={{ mb: 1.5 }}>
                      Base English copy is within store field limits.
                    </Alert>
                  )}
                  {getEditableFieldConfigs(baseReviewPlatform).map((field) =>
                    renderCreateBaseFieldCard(
                      field,
                      activeBaseDraftContent,
                      baseReviewPlatform,
                    ),
                  )}
                </Paper>
              )}
            </Box>
          ) : createListingStage === "success" ? (
            <Box sx={{ mt: 1.5, py: 0.5 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Your listing job finished successfully and the generated version
                is ready for preview, editing, export, and publishing.
              </Alert>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.25,
                  borderRadius: 2.5,
                  borderColor: "#d8f1df",
                  bgcolor: "rgba(240,253,244,0.68)",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: "wrap",
                    mb: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {formData.appName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Generated on {formatListingDateLabel(new Date())}
                    </Typography>
                  </Box>
                  <Chip
                    color="success"
                    icon={<CheckCircleIcon />}
                    label={`${createdListingsResult.length} version${
                      createdListingsResult.length === 1 ? "" : "s"
                    } created`}
                  />
                </Box>
                <Box
                  sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={formatAppCategoryLabel(formData.category)}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${formData.locales.length} locale${
                      formData.locales.length === 1 ? "" : "s"
                    } selected`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      formData.platform === "both"
                        ? "iOS and Android"
                        : getPlatformLabel(formData.platform)
                    }
                  />
                  {formData.platform !== "android" ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={effectiveIncludeWhatsNew ? "primary" : "default"}
                      label={
                        effectiveIncludeWhatsNew
                          ? "iOS What's New included"
                          : formData.isFirstVersionApp
                            ? "iOS What's New excluded (first version)"
                            : "iOS What's New excluded"
                      }
                    />
                  ) : null}
                  <Chip
                    size="small"
                    variant="outlined"
                    color={hasListingContextNote ? "primary" : "default"}
                    label={listingContextSummaryLabel}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {createdListingsResult.map((listing) => (
                    <Paper
                      key={listing.listingId}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        borderColor: "rgba(34,197,94,0.18)",
                        bgcolor: "rgba(255,255,255,0.7)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 800 }}
                          >
                            {getPlatformLabel(listing.platform)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getListingLocales(listing).length} locale
                            {getListingLocales(listing).length === 1
                              ? ""
                              : "s"}{" "}
                            ready
                          </Typography>
                        </Box>
                        <Box
                          sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            label={getListingVersionLabel(listing)}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSelectedListing(listing);
                              setPreviewLocale(
                                getListingLocales(listing)[0] || "",
                              );
                              setNewListingDialog(false);
                              setTabValue(2);
                              resetCreateDialog();
                            }}
                          >
                            Open Preview
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Paper>
            </Box>
          ) : (
            <Box sx={{ mt: 1.5 }}>
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Generated listings are normalized to plain text to avoid invalid
                character issues in the store submission console. Emojis,
                decorative symbols, smart punctuation, and bracketed keyword
                formats are removed or converted.
              </Alert>
              <Accordion
                expanded={createSections.appSource}
                onChange={(_, expanded) =>
                  setCreateSections((prev) => ({
                    ...prev,
                    appSource: expanded,
                  }))
                }
                sx={{ mb: 1.5 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    1. App Source
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Listing generation requires Privacy Policy and Terms of
                    Service links configured in App Registration for the
                    selected app.
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="AI Model"
                        value={createListingModel}
                        onChange={(e) => setCreateListingModel(e.target.value)}
                        helperText="Choose which AI model to use for listing generation."
                      >
                        {AI_LISTING_MODELS.map((model) => (
                          <MenuItem key={model.value} value={model.value}>
                            {model.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>
                          Select a registered app (optional)
                        </InputLabel>
                        <Select
                          value={selectedApp}
                          label="Select a registered app (optional)"
                          onChange={(e) => handleAppSelection(e.target.value)}
                        >
                          <MenuItem value="">
                            <em>Select a registered app (optional)</em>
                          </MenuItem>
                          {apps.map((app) => (
                            <MenuItem key={app.appId} value={app.appId}>
                              {app.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="App Name"
                        helperText="Required. Used as the root app identity for generated listing content."
                        fullWidth
                        value={formData.appName}
                        onChange={(e) =>
                          setFormData({ ...formData, appName: e.target.value })
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={formData.category}
                          label="Category"
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              category: e.target.value,
                            })
                          }
                        >
                          {APP_CATEGORIES.map((category) => (
                            <MenuItem key={category} value={category}>
                              {formatAppCategoryLabel(category)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={createSections.outputTarget}
                onChange={(_, expanded) =>
                  setCreateSections((prev) => ({
                    ...prev,
                    outputTarget: expanded,
                  }))
                }
                sx={{ mb: 1.5 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    2. Output Target
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <FormControl fullWidth>
                      <InputLabel>Platform</InputLabel>
                      <Select
                        value={formData.platform}
                        label="Platform"
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            platform: e.target.value as
                              | "ios"
                              | "android"
                              | "both",
                          })
                        }
                      >
                        <MenuItem value="ios">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <AppleIcon sx={{ fontSize: 18 }} />
                            iOS App Store
                          </Box>
                        </MenuItem>
                        <MenuItem value="android">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <AndroidIcon sx={{ fontSize: 18 }} />
                            Google Play
                          </Box>
                        </MenuItem>
                        <MenuItem value="both">Both Platforms</MenuItem>
                      </Select>
                    </FormControl>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        borderColor: "divider",
                        bgcolor: "rgba(248,250,252,0.7)",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 800, mb: 0.5 }}
                      >
                        iOS URL Localization
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.25 }}
                      >
                        Provide base URLs to localize `supportUrl` and
                        `marketingUrl` across each selected iOS locale. Support
                        URL is required for App Store version metadata.
                      </Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            type="url"
                            label="Support URL (required for iOS)"
                            placeholder="https://example.com/support"
                            helperText="Used for localized App Store `supportUrl` per locale."
                            value={formData.supportUrl}
                            onChange={(event) =>
                              setFormData((prev) => ({
                                ...prev,
                                supportUrl: event.target.value,
                              }))
                            }
                            required={formData.platform !== "android"}
                            disabled={formData.platform === "android"}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            type="url"
                            label="Marketing URL"
                            placeholder="https://example.com"
                            helperText="Used for localized App Store `marketingUrl` per locale."
                            value={formData.marketingUrl}
                            onChange={(event) =>
                              setFormData((prev) => ({
                                ...prev,
                                marketingUrl: event.target.value,
                              }))
                            }
                            required={formData.platform !== "android"}
                            disabled={formData.platform === "android"}
                          />
                        </Grid>
                      </Grid>
                      {formData.platform === "android" ? (
                        <Alert severity="info" sx={{ mt: 1.25 }}>
                          App Store support and marketing URLs are hidden for
                          Android-only generation.
                        </Alert>
                      ) : null}
                    </Paper>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        borderColor: "divider",
                        bgcolor: "rgba(248,250,252,0.7)",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 800, mb: 0.5 }}
                      >
                        iOS What's New Release Notes
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.25 }}
                      >
                        Add localized update notes to each generated iOS locale.
                        Turn this off for first app versions where App Store
                        Connect does not accept What's New.
                      </Typography>
                      <FormControlLabel
                        sx={{ mb: 0.25 }}
                        control={
                          <Checkbox
                            checked={formData.isFirstVersionApp}
                            onChange={(event) =>
                              setFormData((prev) => ({
                                ...prev,
                                isFirstVersionApp: event.target.checked,
                              }))
                            }
                            disabled={formData.platform === "android"}
                          />
                        }
                        label="This is the first App Store version (no What's New)"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.includeWhatsNew}
                            onChange={(event) =>
                              setFormData((prev) => ({
                                ...prev,
                                includeWhatsNew: event.target.checked,
                              }))
                            }
                            disabled={
                              formData.platform === "android" ||
                              formData.isFirstVersionApp
                            }
                          />
                        }
                        label="Include What's New in generated iOS listings"
                      />
                      {formData.platform === "android" ? (
                        <Alert severity="info" sx={{ mt: 1.25 }}>
                          iOS release notes are hidden for Android-only
                          generation.
                        </Alert>
                      ) : null}
                      {formData.isFirstVersionApp &&
                      formData.platform !== "android" ? (
                        <Alert severity="info" sx={{ mt: 1.25 }}>
                          What's New is automatically excluded because first App
                          Store versions cannot publish release notes. Turn off
                          first-version mode for app updates.
                        </Alert>
                      ) : null}
                      {effectiveIncludeWhatsNew ? (
                        <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                          <Grid item xs={12}>
                            <TextField
                              select
                              fullWidth
                              label="What's New template"
                              value={formData.whatsNewTemplate}
                              onChange={(event) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  whatsNewTemplate: event.target
                                    .value as WhatsNewTemplateKey,
                                }))
                              }
                            >
                              {WHATS_NEW_TEMPLATE_OPTIONS.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {
                                getWhatsNewTemplate(formData.whatsNewTemplate)
                                  .description
                              }
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              label={
                                formData.whatsNewTemplate === "custom"
                                  ? "Custom What's New base text"
                                  : "Template override (optional)"
                              }
                              placeholder={
                                formData.whatsNewTemplate === "custom"
                                  ? "Example: We added offline mode and improved notification controls."
                                  : getWhatsNewTemplate(
                                      formData.whatsNewTemplate,
                                    ).baseText
                              }
                              helperText="This source note is translated and adapted into each selected iOS locale."
                              value={formData.whatsNewCustomText}
                              onChange={(event) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  whatsNewCustomText: event.target.value,
                                }))
                              }
                            />
                          </Grid>
                        </Grid>
                      ) : null}
                    </Paper>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        borderColor: "divider",
                        bgcolor: "rgba(248,250,252,0.7)",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 800, mb: 0.5 }}
                      >
                        Listing Context Note (Optional)
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.25 }}
                      >
                        Append a localized context note at the end of each
                        generated long description (iOS Description / Android
                        Full Description) for scenarios like medical
                        disclaimers, subscription requirements, or custom legal
                        notes.
                      </Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel>Context scenarios</InputLabel>
                            <Select
                              multiple
                              value={formData.listingContextTemplates}
                              label="Context scenarios"
                              onChange={(event) => {
                                const value = event.target.value;
                                const normalizedValues = Array.isArray(value)
                                  ? (value as ListingContextPresetKey[])
                                  : (String(value)
                                      .split(",")
                                      .map((item) => item.trim())
                                      .filter(
                                        Boolean,
                                      ) as ListingContextPresetKey[]);
                                setFormData((prev) => ({
                                  ...prev,
                                  listingContextTemplates: normalizedValues,
                                }));
                              }}
                              renderValue={(selected) => {
                                const selectedItems =
                                  selected as ListingContextPresetKey[];
                                if (!selectedItems.length) {
                                  return "No preset selected";
                                }
                                return selectedItems
                                  .map(
                                    (templateKey) =>
                                      getListingContextTemplate(templateKey)
                                        .label,
                                  )
                                  .join(", ");
                              }}
                            >
                              {LISTING_CONTEXT_TEMPLATE_OPTIONS.map(
                                (option) => (
                                  <MenuItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    <Checkbox
                                      checked={formData.listingContextTemplates.includes(
                                        option.value,
                                      )}
                                    />
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {option.label}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {option.description}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ),
                              )}
                            </Select>
                          </FormControl>
                          <Typography variant="caption" color="text.secondary">
                            You can select multiple presets (for example medical
                            disclaimer + hard paywall). They will all be
                            appended and localized.
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Custom context note (optional)"
                            placeholder="Example: Subscription is required to access premium tracking, reminders, and reports."
                            helperText="Optional additional note appended after selected presets. This is localized for all selected locales."
                            value={formData.listingContextCustomText}
                            onChange={(event) =>
                              setFormData((prev) => ({
                                ...prev,
                                listingContextCustomText: event.target.value,
                              }))
                            }
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={createSections.appContext}
                onChange={(_, expanded) =>
                  setCreateSections((prev) => ({
                    ...prev,
                    appContext: expanded,
                  }))
                }
                sx={{ mb: 1.5 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    3. App Context
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Target Audience"
                        fullWidth
                        value={formData.targetAudience}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            targetAudience: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Key Feature"
                        fullWidth
                        value={formData.features[0]}
                        onChange={(e) => {
                          const newFeatures = [...formData.features];
                          newFeatures[0] = e.target.value;
                          setFormData({ ...formData, features: newFeatures });
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {formData.platform !== "android" ? (
                        <TextField
                          label="Keyword"
                          helperText="Used for iOS listing generation only. Plain text only."
                          fullWidth
                          value={formData.keywords[0]}
                          onChange={(e) => {
                            const newKeywords = [...formData.keywords];
                            newKeywords[0] = e.target.value;
                            setFormData({ ...formData, keywords: newKeywords });
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: "100%",
                            minHeight: 56,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Alert severity="info" sx={{ width: "100%" }}>
                            Keywords are only used for iOS listing generation.
                          </Alert>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={createSections.locales}
                onChange={(_, expanded) =>
                  setCreateSections((prev) => ({ ...prev, locales: expanded }))
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    4. Locales
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 1.2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Selected: {formData.locales.length}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <ToggleButtonGroup
                        value={localeSupportFilter}
                        exclusive
                        size="small"
                        onChange={(
                          _event,
                          value: StoreLocaleSupportFilter | null,
                        ) => {
                          if (value) {
                            setLocaleSupportFilter(value);
                          }
                        }}
                      >
                        <ToggleButton value="common">
                          Common to both
                        </ToggleButton>
                        <ToggleButton value="google_play">
                          Google only
                        </ToggleButton>
                        <ToggleButton value="app_store">
                          Apple only
                        </ToggleButton>
                        <ToggleButton value="all">All locales</ToggleButton>
                      </ToggleButtonGroup>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            locales: Array.from(
                              new Set([
                                ...prev.locales,
                                ...filteredAvailableLanguages.map(
                                  (l) => l.code,
                                ),
                              ]),
                            ) as string[],
                          }))
                        }
                      >
                        Select Visible
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            locales: prev.locales.filter(
                              (code) =>
                                !filteredAvailableLanguages.some(
                                  (language) => language.code === code,
                                ),
                            ),
                          }))
                        }
                      >
                        Clear Visible
                      </Button>
                    </Box>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Find locale"
                    placeholder="Type locale name or code"
                    value={localeSearchQuery}
                    onChange={(event) =>
                      setLocaleSearchQuery(event.target.value)
                    }
                    sx={{ mb: 1.5 }}
                  />
                  <FormGroup>
                    {filteredAvailableLanguages.length === 0 && (
                      <Alert severity="info" sx={{ mb: 1.5 }}>
                        No locales match the current filter.
                      </Alert>
                    )}
                    <Grid container spacing={0.5}>
                      {filteredAvailableLanguages.map((language) => (
                        <Grid item xs={12} sm={6} md={4} key={language.code}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.locales.includes(
                                  language.code,
                                )}
                                onChange={() =>
                                  handleLocaleToggle(language.code)
                                }
                              />
                            }
                            label={`${language.flag} ${getStoreLocaleDisplayLabel(language.code)}`}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (
                createListingStage === "generating" ||
                createListingStage === "generating_base"
              )
                return;
              setNewListingDialog(false);
              resetCreateDialog();
            }}
          >
            {createListingStage === "success" ? "Close" : "Cancel"}
          </Button>
          {createListingStage === "success" ? (
            <Button
              variant="contained"
              onClick={() => {
                setNewListingDialog(false);
                setTabValue(2);
                resetCreateDialog();
              }}
            >
              Go to Listing Preview
            </Button>
          ) : createListingStage === "form" ? (
            <Button
              onClick={handleGenerateBaseListingDraft}
              variant="contained"
              disabled={loading || !formData.appName}
            >
              {loading ? <CircularProgress size={24} /> : "Review Base English"}
            </Button>
          ) : createListingStage === "base_review" ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setCreateListingStage("form")}
                disabled={loading}
              >
                Back to Inputs
              </Button>
              <Button
                onClick={handleCreateListing}
                variant="contained"
                disabled={loading || !activeBaseDraftContent}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  "Generate Locales from Base"
                )}
              </Button>
            </Box>
          ) : null}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppStoreListingPage;
